import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { DatePicker } from '@/components/ui/date-picker.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { Check, ChevronLeft, ChevronRight, Plus, Trash2, AlertTriangle, GitBranch } from 'lucide-react';
import { ApiError } from '@/lib/api.js';
import { cn } from '@/lib/utils.js';
import {
  forkQuote, getQuote, getQuotePrefill, patchQuote, addQuoteOption, deleteQuoteOption,
  setPilotMembership, forkQuoteWithoutPilot } from '@/services/quotes.service.js';
import { AIRCRAFT_USE_OPTIONS } from '@/lib/aircraftUsage.js';
import {
  PART_TYPE_PICKLIST, coverageTypeOptions, OPW_CERTS, OPW_RATINGS,
  MEDICAL_QUICK, LIAB_OCC_QUICK, LIAB_PAX_QUICK,
  toNumber, fmtMoney, parseDeductible, deductiblePreview,
  daysBetweenISO, addMonthsClamped,
} from '@/lib/quoteFields.js';
import { inputClass, MoneyInput, AutoTextarea, Field, Helper, QuickPicks } from '@/components/quote/QuoteControls.jsx';

// Deductibles are TEXT and legitimately hold "10%" as well as "27500", so this
// formats ONLY a bare number and passes everything else through untouched. It
// exists because the override note printed one side formatted and the other
// raw ("$27,500, not the 20625 above"), which made two comparable numbers look
// like different kinds of thing.
function displayDeductible(v) {
  if (v === null || v === undefined || String(v).trim() === '') return 'not stated';
  const s = String(v).trim();
  const bare = s.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d+)?$/.test(bare)) return s;      // "10%", "5 %", anything else
  return `$${Number(bare).toLocaleString('en-US')}`;
}
// The tax arithmetic and every reason it refuses. Shared with the node tests, so
// what the browser offers and what the gate pins are one function.
import { taxSuggestion } from '@/lib/stateTax.js';
import {
  WIZARD_STEPS, FIELD_LABELS, computeQuoteCompleteness, FIELD_TO_STEP,
} from '@/lib/quoteCompleteness.js';
import TermPicker, { termMonthsOf, TERM_MAX_MONTHS } from '@/components/TermPicker.jsx';
// The payload rules live apart from the view so they can be tested in node.
import {
  STEP_HINTS, premiumDelta, opwWarning, formFromDetail, rowsFromDetail,
  bodyForStep, withoutUntouchedDefaults, withImpliedSplit, defaultsToAsk, confirmDefaultsPatch, completenessInput, coverageDefaults, derivedTaxesAndFees,
  encodeApprovalStatus, liabilityLegsOf, medicalLegsOf, usesAxes,
} from '@/lib/quoteWizardModel.js';
// The grid rule and the price of a combination, from the SAME module the server
// requires (services/quoting-engine.service.js). The wizard renders the axes the
// server derived and previews what it is about to store; it does not decide
// either. One Fact One Derivation: two copies of "what does this corner cost"
// is the shape that put $9,287 against $1M/$100K limits on the Tower card.
import {
  buildCorners, cornerTotal, pricingBlockers, describeLiability, describeMedical,
} from '@/lib/quoteAxes.js';

// THE PILOT DOOR, NOT A COPY OF IT (item 10, 2026-07-30). David, emphasis his:
// "we also need a way to remove a pilot and add a pilot at this point. NOT A
// DIFFERENT DOOR. same doorway to the pilot history forms." RosterSheet is that
// doorway: its add card create-or-matches the CRM Contact, handles the 409
// duplicate, only emails on an explicit invite choice, and routes a bound-policy
// removal into the LPR release gate. PUT /deals/:id/roster is THE roster
// mutation path and this file adds no second one.
const RosterSheet = lazy(() => import('./RosterSheet.jsx'));

// QuoteWizardSheet. QUOTE-WIZARD-SPEC phase 1: the wizard, no viewer.
//
// WHAT THIS IS NOT. It is not a second quote-entry form. QuoteEntrySheet already
// asks every one of these questions and its field logic is correct and hard won
// (the OPW type-hours free text, the NPO invariant, the per-aircraft coverage
// override, the premium decomposition). Every control here is imported from the
// same two modules that sheet uses. What changes is the SHAPE of the asking, not
// the data and not a single field's meaning.
//
// WHERE IT SITS. David, 2026-07-29: "i think it makes sense to quick enter a
// number and then go through the wizard with only competitive quotes." The
// markets ladder's one-number entry is untouched and stays the fast path most
// quotes are logged through. This opens on a row that ALREADY has a quote, for
// the two or three in contention. It therefore only ever PATCHes: there is no
// create path, and `carrier` is never in a body it sends, because the carrier
// snapshot is written once by the resolver and a different carrier is a
// different quote (70c02bd).
//
// NOTHING BLOCKS. David, same day: "save and just flag as incomplete." Every
// step saves on advance, closing saves the open step, and a quote parked at any
// depth is a legitimate quote. Completeness is DERIVED (lib/quoteCompleteness.js)
// and re-entry lands on the first unanswered step rather than back at the top.
//
// ABSENCE IS NEVER RENDERED AS AN ANSWER. Selects carry an explicit blank option
// and the smooth/split toggle starts with NEITHER side lit, because a quote that
// has not said is different from a quote that said split. This is the one place
// the wizard deliberately diverges from QuoteEntrySheet, which defaults those
// controls: a default is invisible on a form somebody is filling in and becomes
// a claim the moment it is saved.
//
// `dealId` is a PARAMETER, not the wizard's identity. It fetches the deal's
// aircraft and pilots for the per-tail and per-pilot rows, and that is all it
// does. The wizard is built around a quote (TRACON-COMPOSITION-MODEL-SPEC: the
// Deal may one day be the commit output rather than the container).

// Namespace for a per-aircraft field inside the one `defaulted` set. `rows` are
// keyed by index because that is what setRow already takes.
const rowKey = (i, field) => `row:${i}:${field}`;

// initialStep / focusField / initialOptionId / presentMissing: the present
// sheet opens the wizard on the item the server named (decision 13). Absent,
// the wizard behaves exactly as it always has.
export default function QuoteWizardSheet({ dealId, quoteId, open, onOpenChange, onSaved, initialStep = null, focusField = null, initialOptionId = null, presentMissing = null }) {
  const bodyRef = useRef(null);
  const focusedRef = useRef(false);
  const [detail, setDetail] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(null);
  const [rows, setRows] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  // Which coverage fields the FORM filled in rather than the broker (item 5).
  // Without this the defaults would be the very thing this file's header
  // forbids: a value nobody stated, rendered exactly like one somebody did.
  const [defaulted, setDefaulted] = useState(() => new Set());
  const [rosterOpen, setRosterOpen] = useState(false);
  const [optionBusy, setOptionBusy] = useState(false);
  // Set when a limit change would remove combinations the client can currently
  // see. Holds the server's sentence and the rows it named, so the ask is
  // specific rather than "are you sure".
  const [withdrawAsk, setWithdrawAsk] = useState(null);
  // Untouched defaults on the step being left: { keys, after, extra } while the
  // ask is open. `declinedDefaults` holds the ones he chose to leave unstated,
  // so the same page does not ask twice; it resets when another quote loads.
  const [defaultsAsk, setDefaultsAsk] = useState(null);
  const [declinedDefaults, setDeclinedDefaults] = useState(() => new Set());
  // WHICH OPTION IS OPEN (prompt 139, the picker). Null means "the primary",
  // which is what every caller of this sheet has always got. Held as an ID and
  // never as an ordinal: option_number repeats on the same quote.
  const [openOptionId, setOpenOptionId] = useState(null);
  // Which quote the sheet is currently showing, so the loader can tell a tab
  // switch from opening a different quote.
  const loadedQuoteRef = useRef(null);

  // The six-month offer is a second OPTION under this quote. It is the only
  // thing in this wizard that creates a row, and it creates an option, not a
  // quote: the create-free PATCH-only identity is about QUOTES and is intact.
  const reloadQuote = useCallback(async () => {
    try { setDetail(await getQuote(quoteId, openOptionId)); } catch (_) { /* the sheet keeps what it has */ }
  }, [quoteId, openOptionId]);

  // onAddOption IS GONE (2026-08-07). It posted a hand-keyed sibling option; the
  // `+` on the Coverage page is that job, and applyQuoteAxes writes the whole
  // corner set from the two limit lists rather than one row at a time. The
  // POST /quotes/:id/options door itself is untouched and still serves the
  // six-month variant below.

  const onAddSixMonth = useCallback(async (premiumDraft) => {
    const premium = toNumber(premiumDraft);
    // NO OPTION UNTIL A REAL NUMBER IS TYPED. The backend refuses a premiumless
    // option too (400), and this is the same rule stated where he can see it.
    if (premium == null) return false;
    setOptionBusy(true);
    try {
      const primary = (detail?.options || []).find((o) => o.is_primary);
      if (!primary) { toast.error('This quote has no option to vary yet.'); return false; }
      await addQuoteOption(quoteId, {
        // `premium`, not `total_premium`: mapBodyToOption reads body.premium and
        // the engine 400s a premiumless option, so the wrong key here would have
        // read as "you did not type a premium".
        premium,
        term_months: 6,
        // NO option_label. It wrote '6-month', which only restates term_months,
        // and the client card then carried the label pill, a "6-Month Term"
        // pill and a lit "6-Month" toggle: three statements of one fact
        // (David, 2026-08-07). The term is a column; the card reads it.
        term_variant_of: primary.id,
      });
      await reloadQuote();
      toast.success('Six-month premium added. Coverage follows the annual option.');
      return true;
    } catch (e) {
      toast.error(e.message || 'Could not add the six-month premium');
      return false;
    } finally { setOptionBusy(false); }
  }, [quoteId, detail, reloadQuote]);

  // DELETE THE OPEN OPTION. Same door as the six-month remove; the difference is
  // only which option, and the picker names it rather than inferring it.
  // Re-opens the primary afterwards, because the option the sheet was scoped to
  // no longer exists and leaving openOptionId pointing at it would 400 on the
  // next load (resolveOptionTarget refuses an id that is not on the quote).
  const onDeleteOption = useCallback(async (opt) => {
    if (!opt?.id) return;
    setOptionBusy(true);
    try {
      await deleteQuoteOption(quoteId, opt.id);
      setOpenOptionId(null);
      await reloadQuote();
      toast.success('Option deleted.');
    } catch (e) {
      toast.error(e.message || 'Could not delete the option');
    } finally { setOptionBusy(false); }
  }, [quoteId, reloadQuote]);

  const onRemoveSixMonth = useCallback(async (optionId) => {
    setOptionBusy(true);
    try {
      await deleteQuoteOption(quoteId, optionId);
      await reloadQuote();
      toast.success('Six-month premium removed.');
    } catch (e) {
      toast.error(e.message || 'Could not remove the six-month premium');
    } finally { setOptionBusy(false); }
  }, [quoteId, reloadQuote]);

  // The pilot rows this wizard renders come from the prefill, so a pilot added
  // or removed through the roster door has to land here without a reload.
  // Prefill only: the QUOTE did not change, and re-reading it would throw away
  // whatever step he has open.
  const reloadPrefill = useCallback(() => {
    if (!dealId) return;
    getQuotePrefill(dealId, quoteId).then((pf) => setPrefill(pf)).catch(() => {});
  }, [dealId, quoteId]);

  // WHO IS ON THIS OFFER. Two acts behind one control, decided by where the
  // broker is, never by asking him to describe the difference (David: "not sure
  // i'm understanding how to describe diff"). On an ALTERNATE this edits the
  // quote in front of him. On an ORIGINAL it forks, leaving that quote exactly
  // as it was, because a quote keeps the risk makeup it was rated on.
  //
  // Never touches the deal, the PHF or CRM. That is the other door.
  const [membershipBusy, setMembershipBusy] = useState(null);
  const onSetMembership = useCallback(async (pilot, onQuote) => {
    if (!quoteId || !pilot?.contact_id) return;
    const key = pilot.contact_id || pilot.roster_key;
    setMembershipBusy(key);
    try {
      // FLAT, not nested. getQuoteDetail returns a flat payload; `detail.quote`
      // does not exist, so this read was always false and every membership
      // change on an ALTERNATE would have forked instead of editing it.
      const isAlt = Boolean(detail?.alternate_of);
      if (onQuote === false && !isAlt) {
        const res = await forkQuoteWithoutPilot(quoteId, {
          contact_id: pilot.contact_id, name: pilot.name || '',
        });
        toast.success(`Alternate created without ${pilot.name || 'that pilot'}. Enter the carrier's new premium.`);
        // A RISK-CHANGING FORK IS BORN UNPRICED, so send him to the offer step
        // of the new quote rather than leaving him on a quote he did not change.
        (res?.warnings || []).forEach((w) => toast.info(w));
        onSaved?.(res?.quote_id || null);
        onOpenChange?.(false);
        return;
      }
      const res = await setPilotMembership(quoteId, {
        contact_id: pilot.contact_id, name: pilot.name || '', on_quote: onQuote === true,
      });
      // A SERVER `warnings` ARRAY IS ONLY REAL IF A SURFACE RENDERS IT.
      (res?.warnings || []).forEach((w) => toast.info(w));
      toast.success(onQuote
        ? `${pilot.name || 'Pilot'} is on this quote again.`
        : `${pilot.name || 'Pilot'} is off this quote. He stays on the deal.`);
      reloadPrefill();
      reloadQuote();
    } catch (e) {
      toast.error(e?.message || 'Could not change who is on this quote.');
    } finally {
      setMembershipBusy(null);
    }
  }, [quoteId, detail, reloadPrefill, reloadQuote, onSaved, onOpenChange]);

  const set = useCallback((patch) => {
    setForm((f) => ({ ...f, ...patch }));
    // Touching a defaulted field is him answering it. The marking goes.
    setDefaulted((d) => {
      if (!d.size) return d;
      const touched = Object.keys(patch).filter((k) => d.has(k));
      if (!touched.length) return d;
      const next = new Set(d);
      for (const k of touched) next.delete(k);
      return next;
    });
  }, []);
  // Per-row defaults share the SAME set, namespaced. The state tax chip fills a
  // per-tail figure and it has to be marked exactly as a header default is:
  // "$102, the tax table computed it" and "$102, the carrier said so" are
  // different facts, and a per-row convention of its own would be a second
  // answer to a question this file already answers.
  const setRow = useCallback((i, patch) => {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    setDefaulted((d) => {
      if (!d.size) return d;
      const touched = Object.keys(patch).map((k) => rowKey(i, k)).filter((k) => d.has(k));
      if (!touched.length) return d;
      const next = new Set(d);
      for (const k of touched) next.delete(k);
      return next;
    });
  }, []);

  // Setting a row value FROM a suggestion: the same write, plus the marking.
  // Kept apart from setRow so that an ordinary keystroke can never accidentally
  // mark a field as computed.
  const setRowDefaulted = useCallback((i, patch) => {
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    setDefaulted((d) => {
      const next = new Set(d);
      for (const k of Object.keys(patch)) next.add(rowKey(i, k));
      return next;
    });
  }, []);

  // Same, for the header fields. The single-aircraft tax suggestion lands here.
  const setDefaultedField = useCallback((patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setDefaulted((d) => {
      const next = new Set(d);
      for (const k of Object.keys(patch)) next.add(k);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!open || !quoteId) return undefined;
    let dead = false;
    // SWITCHING A TAB IS NOT OPENING THE SHEET (2026-08-07). This blanked
    // everything and rebuilt it, so every tab click made the whole wizard
    // vanish and reappear, and it threw away the step David was standing on.
    // Blanking is right when the QUOTE changes and wrong when only the option
    // does: the sheet is already the right sheet, and one option's fields are
    // being swapped for another's.
    const switchingTab = loadedQuoteRef.current === quoteId;
    if (!switchingTab) {
      setDetail(null); setForm(null); setRows([]); setLoadError(''); setStepIndex(0);
      setDefaulted(new Set()); setDeclinedDefaults(new Set()); setDefaultsAsk(null);
    } else {
      setLoadError('');
    }
    loadedQuoteRef.current = quoteId;
    Promise.all([getQuote(quoteId, openOptionId), getQuotePrefill(dealId, quoteId).catch(() => null)])
      .then(([q, pf]) => {
        if (dead) return;
        const loaded = formFromDetail(q);
        // Fill the coverage step's blanks, and remember which ones were blank.
        // Applied to what the QUOTE said, so nothing the carrier answered is
        // touched; the deal only supplies what the quote left empty.
        // The carrier's standard territory rides in alongside the deal prefill;
        // it comes off the QUOTE detail (the carrier is a property of the quote,
        // not of the deal) but feeds the same defaults function.
        const fill = coverageDefaults(loaded, { ...(pf || {}), carrier_default_territory: q.carrier_default_territory,
          carrier_default_enhancements: q.carrier_default_enhancements });
        const nextForm = { ...loaded, ...fill };
        const nextRows = rowsFromDetail(q, pf);
        setDetail(q); setPrefill(pf); setForm(nextForm); setRows(nextRows);
        setDefaulted(new Set(Object.keys(fill)));
        // Resume where the work is. A quote three steps in reopens on step four,
        // not on the premium it already carries.
        // STAY ON THE STEP HE IS ON when only the option changed. Jumping him to
        // another page because the new option happens to be less complete is the
        // same defect as blanking the sheet, one layer up.
        // An item from the present sheet names its step (initialStep).
        if (!switchingTab) {
          const named = initialStep ? WIZARD_STEPS.findIndex((w) => w.key === initialStep) : -1;
          const { firstIncompleteIndex } = computeQuoteCompleteness(completenessInput(nextForm, nextRows, pf?.pilots));
          setStepIndex(named > -1 ? named : (firstIncompleteIndex === -1 ? 0 : firstIncompleteIndex));
        }
      })
      .catch((e) => { if (!dead) setLoadError(e.message || 'Could not load the quote'); });
    return () => { dead = true; };
    // openOptionId is a dependency: switching option re-loads and re-seeds the
    // whole form from that option. It must NOT be merged into the open form,
    // because a half-swapped form would show option 2's premium beside option
    // 1's deductibles and the save would write that mixture.
  }, [open, quoteId, dealId, openOptionId, initialStep]);

  // Reopening the sheet on a different quote must not carry the previous
  // quote's option id, which would 400 (resolveOptionTarget refuses an id that
  // is not on the quote) instead of simply opening the primary.
  useEffect(() => {
    // An item from the present sheet names its option (initialOptionId);
    // otherwise the primary, as every caller has always got.
    setOpenOptionId(open && initialOptionId ? initialOptionId : null);
    // Closing the sheet must forget which quote was loaded, or reopening the
    // SAME quote would be mistaken for a tab switch and skip the blank + resume.
    if (!open) loadedQuoteRef.current = null;
  }, [quoteId, open, initialOptionId]);

  // AN ITEM OPENS ITS FIELD (decision 13): once the step it names is on screen,
  // the field the server named is scrolled to and focused, once per open.
  useEffect(() => {
    if (!open || !form || !focusField || focusedRef.current) return;
    const el = bodyRef.current?.querySelector(`[data-field~="${focusField}"]`);
    if (!el) return;
    focusedRef.current = true;
    if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
    el.querySelector('input, select, textarea, button')?.focus?.();
  }, [open, form, focusField, stepIndex]);
  useEffect(() => { if (!open) focusedRef.current = false; }, [open]);

  // Memoised because saveStep depends on it: a fresh [] every render would
  // rebuild the callback (and every button bound to it) on every keystroke.
  // WHO IS ON THIS OFFER, split by the SERVER (quote-prefill `quote_roster`).
  // The browser renders the answer and never derives it, so there is only ever
  // one implementation of the rule. `on` is null when the server could not
  // establish it, or when no quote is being edited yet, and then the plain deal
  // roster stands: a new quote has excluded nobody.
  const pilots = useMemo(
    () => prefill?.quote_roster?.on || prefill?.pilots || [],
    [prefill],
  );
  // Named on this quote and NOT on the deal any more. Normally empty. When it is
  // not, it is the state that has no other door: the exclusion is only visible
  // and liftable here.
  const excludedPilots = useMemo(() => prefill?.quote_roster?.off || [], [prefill]);
  const completeness = useMemo(
    () => (form ? computeQuoteCompleteness(completenessInput(form, rows, pilots)) : null),
    [form, rows, pilots]
  );
  const step = WIZARD_STEPS[stepIndex];

  // Save the open step, then run `after`. Every advance, every jump and the
  // close all go through here, which is what "leaving loses nothing" means.
  // `extra` is merged into the body for THIS save only (the withdraw
  // confirmation). It is never held on the form, because a standing permission
  // to pull client-visible options is not what he agreed to.
  // `f` and `d` are passed rather than read from state so a just-confirmed
  // default saves in the same click (state set in that click is not yet visible).
  const runSave = useCallback(async (f, d, after, extra) => {
    // A term that is not a whole number of months is refused here, as the log
    // doors refuse it (statedTermMonths), never stored as a term nobody picked.
    // A default he has not touched on a field the gate asks is not his answer
    // (PG-1): it stays on screen, tagged, and is not sent.
    const draft = withoutUntouchedDefaults(bodyForStep(step.key, f, rows, pilots), d);
    if (draft.term_months !== undefined && termMonthsOf(draft.term_months) == null) {
      toast.error(`The term must be a whole number of months, 1 to ${TERM_MAX_MONTHS}.`);
      return;
    }
    setSaving(true);
    try {
      const body = { ...draft, ...(extra || {}) };
      // NAME THE OPTION THIS FORM IS SHOWING (prompt 131). The sheet is scoped
      // to one option by display; without this the save was not scoped at all
      // and the server re-resolved its own target. On 2026-08-06 the two
      // disagreed on a live quote and option 1 was given option 2's premium.
      //
      // Sent on every step, not just the priced ones: a header-only patch
      // carries no option fields, so naming the option costs nothing and means
      // no future step can forget. The server refuses an id that is not on the
      // quote rather than falling back.
      if (Object.keys(body).length) {
        await patchQuote(quoteId, f.option_id ? { ...body, option_id: f.option_id } : body);
      }
      setWithdrawAsk(null);
      // The corner set may have been rewritten, so the axes the sheet is holding
      // are no longer what is stored: their loaded signature, their leg keys and
      // any newly generated combinations all come back from the server. Reload
      // rather than trust the form, which is the same rule as reading a write
      // back from the surface that renders it instead of echoing the request.
      if (body.axes) await reloadQuote();
      onSaved?.();
      after?.();
    } catch (err) {
      const b = err instanceof ApiError ? err.body : null;
      // REMOVING A LIMIT CAN PULL SOMETHING THE CLIENT CAN SEE, and that is a
      // client-facing act, so it is asked rather than assumed. The server names
      // the rows and refuses; this turns the refusal into the door, because a
      // surface that reports a blocker and offers no way through it is the shape
      // this project keeps finding.
      if (b?.code === 'AXES_WOULD_WITHDRAW') {
        setWithdrawAsk({ message: b.error, rows: b.withdraws || [] });
        setSaving(false);
        return;
      }
      // Stay put on a failure. Advancing past a step that did not save is how a
      // wizard quietly loses an answer.
      toast.error(b?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [rows, pilots, step, quoteId, onSaved, reloadQuote]);

  // Every advance, jump and close: an untouched default on this page asks first.
  const saveStep = useCallback((after, extra) => {
    if (!form) return;
    const keys = defaultsToAsk(step.key, form, defaulted, declinedDefaults);
    if (keys.length) { setDefaultsAsk({ keys, after, extra }); return; }
    return runSave(form, defaulted, after, extra);
  }, [form, defaulted, declinedDefaults, step, runSave]);

  const answerDefaults = useCallback((confirmed) => {
    const ask = defaultsAsk;
    if (!ask || !form) return;
    setDefaultsAsk(null);
    if (confirmed) {
      const nextForm = { ...form, ...confirmDefaultsPatch(form, ask.keys) };
      const nextDefaulted = new Set(defaulted);
      for (const k of ask.keys) nextDefaulted.delete(k);
      setForm(nextForm); setDefaulted(nextDefaulted);
      runSave(nextForm, nextDefaulted, ask.after, ask.extra);
    } else {
      setDeclinedDefaults((d0) => new Set([...d0, ...ask.keys]));
      runSave(form, defaulted, ask.after, ask.extra);
    }
  }, [defaultsAsk, form, defaulted, runSave]);

  // His yes: the SAME save again with the confirmation attached. Attached to the
  // one request rather than parked on the form, so it cannot survive into a
  // later save and quietly authorise a withdrawal he was not asked about.
  const confirmWithdraw = useCallback(() => {
    setWithdrawAsk(null);
    saveStep(undefined, { confirm_withdraw: true });
  }, [saveStep]);

  const goto = useCallback((i) => saveStep(() => setStepIndex(i)), [saveStep]);
  const close = useCallback(() => saveStep(() => onOpenChange?.(false)), [saveStep, onOpenChange]);

  const carrierLabel = detail?.carrier_name || 'this carrier';
  const remaining = completeness ? completeness.steps.filter((s) => !s.complete).length : 0;

  // FORK AN ALTERNATE, FROM THE MAIN WIZARD (CC PROMPT 31 Part B).
  // David: "when entering the forks, it should be easy to do right from the
  // main wizard." The whole value is that adding the son is ONE CLICK and not a
  // re-key, the same principle as PHF seeding: the memory is the product.
  //
  // An ALTERNATE is a different Fleet + Pilot set. An option (hull value,
  // deductibles, TRIA, term) is NOT this button: those stay on this quote under
  // the same number, which is what "Add option" already does.
  const [forking, setForking] = useState(false);
  const handleFork = useCallback(async () => {
    setForking(true);
    try {
      // Save first: the fork copies the server's row, so an unsaved edit in
      // front of the broker would silently not carry across.
      // saveStep is the wizard's one save path; calling it here means the fork
      // copies what the broker can see rather than the last-loaded row.
      await new Promise((res) => saveStep(res));
      const r = await forkQuote(quoteId);
      const newId = r?.quote_id || r?.id || null;
      toast.success('Alternate created. Change the fleet or the pilots on it.');
      onSaved?.({ forkedTo: newId });
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.body?.error || err?.message || 'Could not create the alternate');
    } finally {
      setForking(false);
    }
  }, [quoteId, saveStep, onSaved, onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <SheetContent side="bottom" className="flex max-h-[96vh] flex-col overflow-hidden md:mx-auto md:max-w-3xl">
        <SheetHeader>
          <SheetTitle>{carrierLabel} quote</SheetTitle>
          <SheetDescription>
            {loadError
              ? loadError
              : !form
                ? 'Loading the quote…'
                : remaining === 0
                  ? 'Every question answered.'
                  : `${remaining} question${remaining === 1 ? '' : 's'} left. Every step saves as you go.`}
          </SheetDescription>
        </SheetHeader>

        {form ? (
          <>
            <StepStrip
              steps={completeness.steps}
              current={stepIndex}
              disabled={saving}
              onJump={goto}
            />

            <div ref={bodyRef} className="flex-1 overflow-y-auto py-3 pr-1">
              {/* THE TABS ARE THE FALLBACK NOW, NOT THE MECHANISM (2026-08-07).
                  A quote whose options are the product of two limit lists is
                  edited as those lists, on the Coverage page, with a `+` each.
                  The tab strip renders ONLY when the stored options are not a
                  grid — separate offers rather than points on one, which is
                  four of the five live multi-option quotes and every one of them
                  correctly so (Global's All Risk / GNIM trio, two Acceleration
                  per-aircraft sets, and the Allianz pair that records its limits
                  in the labels and nowhere else).

                  So the two mechanisms are never both on screen, which is the
                  whole complaint: "why am I still seeing tabs and no plus". */}
              {form.axes_ok === false ? (
                <OptionPicker options={detail?.options} onOpenOption={setOpenOptionId}
                  label={form.option_label} onLabel={(v) => set({ option_label: v })}
                  termsHint={optionTermsHint(form)} reason={form.axes_reason}
                  onDelete={(o) => o && onDeleteOption(o)} />
              ) : null}
              <p className="mb-3 text-[11.5px] text-text-muted">{STEP_HINTS[step.key]}</p>
              <PresentMissingBanner items={presentMissing} onJump={(key) => { const i = WIZARD_STEPS.findIndex((w) => w.key === key); if (i > -1 && i !== stepIndex) goto(i); }} />

              <WithdrawAsk ask={withdrawAsk} onConfirm={confirmWithdraw} onCancel={() => setWithdrawAsk(null)} busy={saving} />
              <DefaultsAsk ask={defaultsAsk} form={form} onAnswer={answerDefaults} busy={saving} />

              {step.key === 'offer' ? (
                <OfferStep form={form} set={set} rows={rows} carrier={carrierLabel} options={detail?.options}
                  onAddSixMonth={onAddSixMonth} onRemoveSixMonth={onRemoveSixMonth} optionBusy={optionBusy}
                  taxContext={prefill?.tax_context} setDefaultedField={setDefaultedField} defaulted={defaulted}
                  incumbent={prefill?.incumbent} effectiveSource={prefill?.deal?.effective_source} />
              ) : step.key === 'terms' ? (
                <TermsStep form={form} set={set} rows={rows} setRow={setRow} setRows={setRows} defaulted={defaulted}
                  taxContext={prefill?.tax_context} setRowDefaulted={setRowDefaulted} />
              ) : step.key === 'pilots' ? (
                <PilotsStep form={form} set={set} pilots={pilots} rows={rows} defaulted={defaulted}
                  excludedPilots={excludedPilots}
                  isAlternate={Boolean(detail?.alternate_of)}
                  onSetMembership={onSetMembership} membershipBusy={membershipBusy}
                  onEditRoster={() => setRosterOpen(true)} />
              ) : (
                <RestStep form={form} set={set} carrierContacts={detail?.carrier_contacts} />
              )}

              <MissingLine step={completeness.steps[stepIndex]} />
            </div>

            <SheetFooter className="border-t border-border bg-surface px-1 py-2">
              <Button
                type="button"
                variant="ghost"
                disabled={saving || forking}
                onClick={handleFork}
                className="gap-1 mr-auto"
                title="A different fleet or pilot set. Copies everything, takes its own quote number, and both stay live."
              >
                <GitBranch size={15} /> {forking ? 'Forking…' : 'Fork alternate'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={saving || stepIndex === 0}
                onClick={() => goto(stepIndex - 1)}
                className="gap-1"
              >
                <ChevronLeft size={15} /> Back
              </Button>
              <Button type="button" variant="outline" disabled={saving} onClick={close}>
                {saving ? 'Saving…' : 'Save and close'}
              </Button>
              {stepIndex < WIZARD_STEPS.length - 1 ? (
                <Button type="button" disabled={saving} onClick={() => goto(stepIndex + 1)} className="gap-1">
                  {saving ? 'Saving…' : 'Next'} <ChevronRight size={15} />
                </Button>
              ) : (
                <Button type="button" disabled={saving} onClick={close}>
                  {saving ? 'Saving…' : 'Done'}
                </Button>
              )}
            </SheetFooter>
          </>
        ) : (
          <p className="py-8 text-sm text-text-muted">{loadError || 'Loading…'}</p>
        )}
      </SheetContent>

      {/* The existing roster door, opened from the pilots step. Nothing about
          the quote is written when it closes; only the pilot list is refetched. */}
      {rosterOpen ? (
        <Suspense fallback={null}>
          <RosterSheet
            dealId={dealId}
            open={rosterOpen}
            onOpenChange={setRosterOpen}
            onChange={reloadPrefill}
            onLprRequired={() => {
              // RosterSheet has already said "Opening the LPR wizard". This
              // surface cannot open it, so say where it lives rather than leave
              // a promise nothing keeps.
              setRosterOpen(false);
              toast.error('Finish the release from the deal actions, under Roster.');
            }}
          />
        </Suspense>
      ) : null}
    </Sheet>
  );
}

// THE INCUMBENT (wave 4 item 2, 2026-07-31). David: editing a renewal quote
// should show what the expiring policy actually was. The wizard showed the new
// quote in isolation, so the one comparison that matters on a renewal was the
// one thing not on screen.
//
// READ-ONLY, AND NOT A DEFAULT. Nothing here is a control and nothing here
// prefills a field. A renewal priced off last year's number rather than this
// year's quote is a worse bug than retyping, so there is deliberately no chip,
// no "use it", and no path from this panel into the form.
//
// ABSENCE IS EXPLICIT. A deal with no resolvable prior says so in a sentence.
// It does not render an empty panel, and a prior policy with no premium recorded
// says "not recorded" rather than showing a blank that reads as free.
function IncumbentPanel({ incumbent }) {
  if (!incumbent) return null;
  if (!incumbent.resolved) {
    // A new-business deal has no incumbent and does not need to be told so.
    if (incumbent.reason === 'not_a_renewal') return null;
    const why = incumbent.reason === 'prior_policy_unreadable'
      ? `The expiring policy (deal ${incumbent.deal_id}) could not be read, so it is not shown.`
      : 'This renewal is not linked to an expiring policy, so there is nothing to compare against.';
    return (
      <div className="rounded-card border border-border bg-bg/40 px-3 py-2 text-[11.5px] text-text-dim">
        {why}
      </div>
    );
  }

  const gap = (v) => (v == null || v === '' ? <span className="text-text-dim">not recorded</span> : null);
  const dates = incumbent.policy_start && incumbent.policy_end
    ? `${incumbent.policy_start} to ${incumbent.policy_end}`
    : null;

  return (
    <div className="space-y-1.5 rounded-card border border-border bg-bg/40 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
        Expiring policy{incumbent.policy_number ? ` ${incumbent.policy_number}` : ''}
      </p>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11.5px] text-text">
        <span><span className="text-text-muted">Carrier</span> {incumbent.carrier || gap(null)}</span>
        <span>
          <span className="text-text-muted">Premium</span>{' '}
          {incumbent.premium == null ? gap(null) : fmtMoney(incumbent.premium)}
        </span>
        <span>
          <span className="text-text-muted">Term</span>{' '}
          {incumbent.term_months == null ? gap(null) : `${incumbent.term_months} months`}
        </span>
        <span><span className="text-text-muted">Dates</span> {dates || gap(null)}</span>
      </div>
      {/* BOTH FACTS, NEVER ONE. The term became a derived value only recently, so
          a policy bound before that carries whatever its bind wrote. When the
          stored term and the stored dates disagree the record is shown as it is
          and the disagreement is named. Recomputing the term to make this panel
          look consistent would be replacing what the record says with what the
          wizard thinks it should have said. */}
      {incumbent.term_matches_dates === false ? (
        <p className="flex items-start gap-1.5 text-[10.5px] text-warning">
          <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
          <span>
            The recorded term and the recorded dates disagree. Both are shown as the record has them.
          </span>
        </p>
      ) : null}
      <p className="text-[10px] text-text-dim">
        For comparison only. Nothing on this quote is filled in from it.
      </p>
    </div>
  );
}

// THE STATE TAX SUGGESTION (wave 4 item 1, 2026-07-31). David, after a day of
// quote entry: the rates for FL, KY, WV and NJ should not be hardcoded and he
// wants somewhere to see and change them.
//
// IT OFFERS AND IT NEVER WRITES, and this is the one place in the wizard where
// that distinction is load-bearing rather than tidy. A territory default is
// wrong on screen and gets corrected. A tax default is wrong on an INVOICE and
// gets paid. So this is a chip he clicks, exactly like the per-aircraft premium
// sum beside it, and the value it lands is marked defaulted until he touches it.
//
// A REFUSAL IS RENDERED, NOT SWALLOWED. "No confirmed rate on file for WV" and
// "KCKB is not in the airport data" are both actionable. A blank field is not,
// and a $0 is worse than either, because $0 of surplus lines tax is a claim.
function TaxChip({ suggestion, onUse }) {
  if (!suggestion) return null;
  if (suggestion.blocked) {
    // The premium simply not being typed yet is not worth a line: he is about to
    // type it, and the chip appears when he does.
    if (suggestion.reason === 'no_premium') return null;
    return (
      <p className="mt-1 text-[10.5px] text-text-dim">
        {suggestion.message}{' '}
        <a href="/admin/taxes" target="_blank" rel="noreferrer" className="underline hover:text-text-muted">
          Rate table
        </a>
      </p>
    );
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10.5px] text-text-muted">
      <span>{suggestion.message} = <strong className="text-text">{fmtMoney(suggestion.amount)}</strong></span>
      <button
        type="button"
        onClick={() => onUse(suggestion.amount)}
        className="rounded-pill border border-border bg-bg px-2 py-0.5 text-[10.5px] text-text hover:border-border-strong"
      >
        Use it
      </button>
    </div>
  );
}

// The progress indicator says what is LEFT, not how far in you are: a completed
// step is a quiet check and an open one is a live dot. No percentage, no "step 3
// of 6", because neither of those tells the broker whether he can stop.
function StepStrip({ steps, current, disabled, onJump }) {
  return (
    <div className="flex flex-wrap gap-1.5 border-b border-border pb-2">
      {steps.map((s, i) => {
        const active = i === current;
        return (
          <button
            key={s.key}
            type="button"
            disabled={disabled}
            onClick={() => (i === current ? undefined : onJump(i))}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-[11.5px] disabled:opacity-50',
              active
                ? 'border-accent bg-accent-soft font-medium text-accent'
                : 'border-border text-text-muted hover:border-border-strong hover:text-text'
            )}
          >
            {s.complete
              ? <Check size={12} strokeWidth={2.4} className="text-success" />
              : <span className="h-1.5 w-1.5 rounded-full bg-text-dim" />}
            {s.title}
          </button>
        );
      })}
    </div>
  );
}

// THE SERVER'S LIST, WHEN THE PRESENT SHEET OPENED THIS (decision 13). The
// items are the present gate's own words; each one jumps to the step that holds
// its field. It is not this wizard's step progress below, which is a different
// question ("which of my questions are answered") and greys nothing.
function PresentMissingBanner({ items, onJump }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <div className="mb-3 rounded-card border border-warning/50 bg-warning-soft px-3 py-2 text-[11.5px] text-warning" data-present-missing>
      <span className="font-medium">Before Present:</span>
      {items.map((item) => (FIELD_TO_STEP[item.field] ? (
        <button key={item.id || item.key} type="button" onClick={() => onJump(FIELD_TO_STEP[item.field])}
          className="ml-2 underline underline-offset-2 hover:text-text">{item.label}</button>
      ) : <span key={item.id || item.key} className="ml-2">{item.label}</span>))}
      <span className="ml-2 text-text-muted">The list is checked again when this closes.</span>
    </div>
  );
}

// What this step is still missing, named. It sits under the fields rather than
// over them so it reads as a summary of what is left, never as a rejection of
// what was typed. It has no colour and no icon: nothing here is wrong.
function MissingLine({ step }) {
  if (!step || step.complete) return null;
  const names = step.missing.map((f) => FIELD_LABELS[f] || f);
  return (
    <p className="mt-4 rounded-card border border-border bg-bg/40 px-3 py-2 text-[11.5px] text-text-muted">
      Still open: {names.join(', ')}.
    </p>
  );
}

// The header block of the quote, in one step: the premium, the carrier's
// reference and the two dates all sit within an inch of each other on the page
// the broker is already looking at, and the carrier is a read-only label rather
// than a question. Splitting this in two (it was "the number" then "the paper"
// until 2026-07-29) bought a page turn and nothing else.
//
// The optional money detail sits BELOW the four fields he came here to type.
// Nothing from the terms step moved up: limits, hull and deductibles are read off
// a different part of the document and stay where they were.
// A stable name for an option that may have no label of its own. Index-based,
// never derived from the premium: two options at the same price are still two
// options, and naming them by money would merge them on screen.
// WHAT TO CALL AN OPTION, and it is never its position.
//
// This returned `Option ${idx + 1}` off the ARRAY INDEX, so the picker showed
// "Option 3" against a row whose option_number is 1 and whose premium is 8,932.
// The array comes back unordered, so the number named nothing at all: the same
// ordinal-identifies-nothing defect the picker was built to avoid, committed by
// the picker's own label.
//
// The broker's own name wins. Otherwise the option describes ITSELF from the
// terms that distinguish it, which is what David asked the client toggle to do.
// Falls back to the premium, which at least identifies a row.
function optionName(o) {
  if (!o) return 'an unlabelled option';
  if (o.option_label) return o.option_label;
  const money = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || !n) return '';
    if (n >= 1e6 && n % 1e5 === 0) return `$${n / 1e6}M`;
    if (n >= 1e3 && n % 100 === 0) return `$${n / 1e3}K`;
    return `$${n.toLocaleString('en-US')}`;
  };
  // ONE NAME EACH. This coalesced the migration's dual column names
  // (liability_csl, per_passenger, medical_per_person) client-side, which is the
  // second-writer tell CLAUDE.md names — and it was reaching for a fallback the
  // payload has never carried either. getQuoteDetail resolves each pair ONCE, on
  // the server, and sends the canonical name; this reads it.
  const occ = money(o.liability_per_occurrence);
  const pax = money(o.liability_per_passenger);
  const med = money(o.medical_payments);
  const parts = [];
  if (occ && pax) parts.push(`${occ}/${pax}`); else if (occ) parts.push(occ);
  if (med) parts.push(`${med} med`);
  if (o.term_months && Number(o.term_months) !== 12) parts.push(`${o.term_months}-mo`);
  if (parts.length) return parts.join(' · ');
  // NOT the premium. Two options at the same price are still two options, and
  // naming by money merges them (tests/quote-options-visible.test.js). Falls
  // back to option_number, which is at least a property of the ROW rather than
  // of the array this happens to sit in.
  return o.option_number != null ? `Option ${o.option_number}` : 'unlabelled option';
}

// sixMonthProRata IS DELETED (2026-08-07), not collapsed and not caveated.
//
// It printed "Pro rata on the day count is $4,429" with a `Use it` button, on
// Milam's MACH 2 quote. 8,932 x 181/365. That is TRACON pricing a six-month
// policy, and pricing it wrong on purpose:
//
//   CARRIERS DO NOT WRITE SHORT-TERM AT PRO RATA. They write it at SHORT RATE,
//   with a minimum earned premium, and it lands well above half. Beacon's own
//   six-month is 1,177 against 2,335 (50.4%, near pro rata), which is exactly
//   why one carrier's behaviour cannot become a button.
//
// `Use it` wrote a premium no underwriter quoted, biased LOW, into a field the
// bind reads. It is the same unquoted-corner rule the limits grid now enforces
// structurally, unenforced one panel over. "Nothing is written until you add
// it" was true and is not sufficient: the number was on screen with a button
// under it.
//
// A six-month premium is typed off a carrier document or it does not exist.

// ── OptionsBlock IS GONE, AND SAYING SO IS THE POINT (2026-08-07) ─────────
//
// It was the duplicate-forward "Add an option" form: a second copy of every
// coverage field, filled from the option on screen, so a broker could hand-key a
// sibling that differed in a limit. THE `+` ON THE COVERAGE PAGE IS THAT JOB
// NOW, and it does it without a second form, without a second copy of the
// fields, and without the broker managing the combinations at all. Two
// mechanisms for one job is what cost David an hour on 2026-08-07, so the
// replaced one leaves in the same commit as its replacement.
//
// WHAT LEAVES WITH IT, NAMED RATHER THAN DISCOVERED IN THREE MONTHS: adding a
// sibling that differs in something OTHER than a limit — a different deductible
// pair, a different hull value, a different coverage basis — no longer has a
// door in this wizard. That is deliberate and it is not nothing:
//
//   * A COVERAGE CONFIGURATION IS NOT MENU-PRICED (the standing ruling). Full
//     Flight to Liability Only is separately rated and surcharged, so it was
//     never a thing to generate combinations across, and Global's All Risk /
//     GNIM trio is exactly that shape.
//   * Those quotes still EDIT: deriveAxes refuses to read them as a grid, the
//     tab strip renders for them, and nothing on file became unreachable.
//   * DEDUCTIBLES ARE PLAUSIBLY A THIRD AXIS ("present 100/500, the client asks
//     what happens if they go up") and are deliberately NOT built here. David's
//     layout named two sections. A third is his call, not a free ride on this.

function SixMonthOffer({ form, options, onAdd, onRemove, busy, offersShortTerm }) {
  const existing = (options || []).find((o) => o.term_variant_of);
  const [draft, setDraft] = useState('');
  // COLLAPSED UNLESS THERE IS ONE (2026-08-07). David: "I keep getting told that
  // the 6-mo option collapses when it's not Beacon. Not so. Here's Mach."
  //
  // He was told that twice and it was never true: <SixMonthOffer/> rendered
  // UNCONDITIONALLY and there has never been a gate of any kind. So the panel
  // sat open on every quote in the book, offering a short-term price on carriers
  // that do not write one.
  //
  // It opens by itself when the quote HAS a six-month, because collapsing a
  // stored value is how a stored value stops being read.
  //
  // WHAT THIS IS NOT: the carrier-config gate Cowork asked for. That needs a
  // `carriers.offers_short_term` flag, and `requires_snkl` (the precedent for a
  // three-state carrier boolean) has no admin field either — so shipping the
  // flag today would be a switch David cannot reach, which is the thing CLAUDE.md
  // names. A disclosure needs no config, hides the offer from Mach 2, and keeps
  // Beacon one click away. The flag is costed in the reply.
  const [open, setOpen] = useState(false);

  // ── THE GATE (Ruling 4) ─────────────────────────────────────────────────
  // A carrier that has not been asked, or has been asked and said no, gets NO
  // six-month control at all — not a collapsed one. `carriers.offers_short_term`
  // is the switch and it lives in the carrier editor, which is the whole point:
  // the flag, its control and this gate ship together, so there is never a
  // moment where a switch exists that David cannot reach.
  //
  // AN EXISTING SIX-MONTH ALWAYS RENDERS, whatever the flag says. Hiding a
  // stored value behind a config change is how a stored value stops being read,
  // and the flag can be turned off after a variant was already added.
  if (!existing && !offersShortTerm) return null;

  if (existing) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-card border border-border bg-bg/40 px-3 py-2">
        <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">Six-month offer</span>
        <span className="text-sm font-medium text-text">{fmtMoney(existing.total_premium)}</span>
        <span className="text-[10.5px] text-text-dim">
          same coverage as the {form.term_months || 12}-month price above, kept in step automatically
        </span>
        <button
          type="button"
          onClick={() => onRemove(existing.id)}
          disabled={busy}
          className="ml-auto inline-flex items-center gap-1 rounded-pill border border-border bg-bg px-2.5 py-0.5 text-[11px] text-text-muted hover:border-border-strong hover:text-text disabled:opacity-50"
        >
          <Trash2 size={12} /> Remove
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[11px] text-text-dim underline decoration-dotted underline-offset-2 hover:text-text-muted"
      >
        Add a six-month premium
      </button>
    );
  }

  return (
    <div className="space-y-1.5 rounded-card border border-border bg-bg/30 p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">Six-month offer</span>
        <div className="w-40">
          <MoneyInput value={draft} onChange={setDraft} placeholder="six-month premium" autoFocus />
        </div>
        <button
          type="button"
          onClick={() => onAdd(draft).then((ok) => { if (ok) { setDraft(''); setOpen(false); } })}
          disabled={busy || toNumber(draft) == null}
          className="rounded-pill border border-accent bg-accent-soft px-2.5 py-1 text-[11px] font-medium text-accent disabled:opacity-40"
        >
          {busy ? 'Adding…' : 'Add'}
        </button>
        <button type="button" onClick={() => { setOpen(false); setDraft(''); }} className="text-[11px] text-text-dim hover:text-text-muted">Cancel</button>
      </div>
      {/* NO SUGGESTED FIGURE. See the note above sixMonthProRata's grave: a
          short-term premium comes off a carrier document, and the only number
          TRACON can compute is one no underwriter quoted. */}
      <p className="text-[11px] text-text-dim">
        Type the premium the carrier quoted for six months. Coverage follows the annual option.
      </p>
    </div>
  );
}

// The tax context for one schedule row: the rate the server resolved for THAT
// tail's base airport, applied to the money that tail currently carries.
function taxSuggestionForTail(taxContext, row) {
  const ctx = taxContext?.by_tail?.[String(row?.tail_number_id || '')];
  if (!ctx) return null;
  return taxSuggestion({
    airport: ctx.airport,
    rateRow: ctx.rate,
    premium: row.aircraft_premium,
    fees: row.fees,
  });
}

// The header's tax context on a SINGLE-aircraft quote, where the per-tail field
// is deliberately hidden (item 8b) and the header is the one editable home.
// There is exactly one tail, so there is exactly one state, and the money is the
// policy premium rather than a per-tail figure.
function taxSuggestionForHeader(taxContext, rows, form) {
  if (!Array.isArray(rows) || rows.length !== 1) return null;
  const ctx = taxContext?.by_tail?.[String(rows[0]?.tail_number_id || '')];
  if (!ctx) return null;
  return taxSuggestion({
    airport: ctx.airport,
    rateRow: ctx.rate,
    premium: form.premium,
    fees: form.admin_fee,
  });
}

// The offer lapse date, out of the way. It opens by itself when the quote
// already carries one, so collapsing the question never hides an answer.
//
// The hook runs before any conditional return, and the initial state is read
// once: a value arriving later (it cannot here, the form loads before this
// mounts) would not reopen it, and that is the only thing this shape gives up.
function OfferExpiry({ value, onChange }) {
  const [open, setOpen] = useState(() => !!value);
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[11px] text-text-dim underline decoration-dotted underline-offset-2 hover:text-text-muted"
      >
        Add an offer expiration
      </button>
    );
  }
  return (
    <div className="md:max-w-[50%]">
      <Field label="Offer expires">
        <DatePicker
          value={value}
          onChange={onChange}
          placeholder="When the offer lapses"
        />
        <Helper>Optional. Not every carrier states one.</Helper>
      </Field>
    </div>
  );
}

// ── THE PICKER, AT SHEET LEVEL AND NOT INSIDE A STEP ──────────────────────
//
// It shipped inside OfferStep, which was wrong and David could not find it. The
// wizard RESUMES AT THE FIRST INCOMPLETE STEP, so a quote that already has its
// premium opens on Coverage or later and the picker was simply not on screen.
// Worse, it vanished the moment he advanced a page while every field on that
// page still belonged to the option it named.
//
// A CONTROL THAT SCOPES THE WHOLE SHEET HAS TO BE VISIBLE ON THE WHOLE SHEET.
// Putting it on one page made "which option am I editing" answerable only from
// the page that happened to hold the answer, which is the same shape as a screen
// that reports a blocking problem and offers no door out of it.
//
// Every control keys on o.id. option_number is an ORDINAL THAT REPEATS: quote
// f8edf033 carries four options numbered 1, 2, 2, 1, three written in one call
// sharing a created_at to the microsecond. Keyed on the number, one click would
// address two rows, and the scoped save would then scope correctly to the wrong
// one.
//
// IT RENDERS ONLY FOR OPTION SETS THAT ARE NOT A GRID (2026-08-07), and it says
// which ones and why. A broker who sees tabs on one quote and a `+` on the next
// is owed the sentence that distinguishes them; without it the surface looks
// like it changes its mind.
function OptionPicker({ options, onOpenOption, label, onLabel, termsHint, onDelete, reason }) {
  const all = Array.isArray(options) ? options : [];
  // One option is the ordinary case and needs no control: the sheet is the
  // option. Rendering a single button would imply a choice that does not exist.
  if (all.length <= 1) return null;
  return (
    <div className="mb-2">
      {/* TABS, not pills. David asked twice; a row of pills reads as filters,
          and this is navigation: the tab IS the option, and everything below it
          belongs to the one that is open. */}
      <div className="flex flex-wrap items-end gap-1 border-b border-border">
        {all.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onOpenOption?.(o.is_primary ? null : o.id)}
            className={`-mb-px rounded-t border border-b-0 px-2.5 py-1.5 text-[12px] ${
              o.is_open
                ? 'border-border bg-surface font-medium text-text'
                : 'border-transparent text-text-muted hover:text-text'
            }`}
          >
            {optionName(o)}
            {o.total_premium != null ? (
              <span className="ml-1.5 text-[11px] text-text-dim">{fmtMoney(o.total_premium)}</span>
            ) : null}
            {o.presented ? <span className="ml-1.5 text-[10px] text-success">shown</span> : null}
          </button>
        ))}
      </div>
      <div className="rounded-b border border-t-0 border-border bg-bg/40 px-2.5 py-2">
      {/* RENAME IT HERE, beside the choice. The label control lived on the Offer
          step, so on a quote that resumed past that page there was no way to
          name an option at all: David hit exactly that and the client card fell
          back to "Option 1". Which option, and what it is called, are one
          question and belong in one place.

          DEFAULTED TO THE TERMS, NEVER FORCED. The placeholder shows what the
          option would be called if he says nothing; blank stays blank rather
          than acquiring a name nobody chose. */}
      <div className="mt-2 flex items-center gap-2">
        <span className="shrink-0 text-[11px] text-text-muted">Name this option</span>
        <Input
          value={label ?? ''}
          onChange={(e) => onLabel?.(e.target.value)}
          placeholder={termsHint || 'optional'}
          className="h-7 flex-1 text-[12px]"
        />
      </div>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <p className="text-[11px] leading-snug text-text-muted">
          {reason
            ? `These are edited one at a time because ${reason}.`
            : 'Every field on this sheet belongs to the open tab. Switching reloads them; it does not carry anything across.'}
        </p>
        {/* DELETE THE OPEN OPTION. Refused on the last one: a quote with no
            option has no price, and the bind reads the option, not the header. */}
        {all.length > 1 && onDelete ? (
          <button
            type="button"
            onClick={() => onDelete(all.find((o) => o.is_open))}
            className="shrink-0 text-[11px] text-danger hover:underline"
          >
            Delete this option
          </button>
        ) : null}
      </div>
      </div>
    </div>
  );
}

// What the option would be CALLED if the broker names nothing. Rendered as a
// placeholder only and never written: the ladder already falls back to the terms
// when the label is blank, so writing it would store a value nobody chose and
// make "he named it that" indistinguishable from "he left it alone".
function optionTermsHint(f) {
  const occ = toNumber(f?.liability_per_occurrence);
  const pax = toNumber(f?.liability_per_passenger);
  if (occ && pax) return `${fmtMoney(occ)} / ${fmtMoney(pax)}`;
  if (occ) return f?.liability_smooth === true ? `${fmtMoney(occ)} CSL` : fmtMoney(occ);
  if (f?.term_months) return `${f.term_months}-month`;
  return '';
}

function OfferStep({ form, set, rows, carrier, options, onAddSixMonth, onRemoveSixMonth, optionBusy, taxContext, setDefaultedField, incumbent, defaulted, effectiveSource }) {
  const mark = (k) => (defaulted?.has(k) ? 'default' : null);
  const allOptions = Array.isArray(options) ? options : [];
  const primaryOption = allOptions.find((o) => o.is_primary) || null;
  const otherOptions = allOptions.filter((o) => !o.is_primary);
  const primaryLabel = primaryOption ? optionName(primaryOption) : 'the only option';
  // ── THE LABEL, PER OPTION ─────────────────────────────────────────────
  // David can rename option 2 and cannot rename option 1, because the label
  // control only ever existed on the add-option form. Same collapse as
  // everything else: the first option is written by the create path, which
  // never offers a label, so 105 of 380 options carry one that identifies
  // nothing ("Option", or null).
  //
  // DEFAULTED TO THE TERMS, NEVER FORCED TO THEM. The placeholder shows what
  // the option would be called if he says nothing; it is not written, so an
  // option he never named stays unnamed rather than acquiring a label nobody
  // chose. A default that writes itself is a placeholder spelled like an answer.
  // Base to base: TRIA is an election and the admin fee is not premium.
  const delta = premiumDelta(form.premium, form.hull_premium, form.liability_premium, form.medical_premium);
  // The premium is DERIVED once the limits branch: see the Premium field below.
  const premiumDerived = usesAxes(form);
  const cornerCount = liabilityLegsOf(form).length * medicalLegsOf(form).length;
  // Non-null only on a fleet. Then the header taxes and fees are its sum and
  // are not editable here; bodyForStep saves the same figures.
  const derived = derivedTaxesAndFees(rows);
  const perTail = rows.reduce((acc, r) => ({
    premium: acc.premium + (toNumber(r.aircraft_premium) || 0),
    taxes: acc.taxes + (toNumber(r.estimated_taxes) || 0),
    fees: acc.fees + (toNumber(r.fees) || 0),
  }), { premium: 0, taxes: 0, fees: 0 });
  const hasPerTail = perTail.premium > 0 || perTail.taxes > 0 || perTail.fees > 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-card border border-border bg-bg/40 px-3 py-2">
        <span className="text-[10px] font-medium uppercase tracking-wide text-text-muted">Carrier</span>
        <span className="text-sm font-medium text-text">{carrier}</span>
        <span className="text-[10.5px] text-text-dim">recorded when this quote was logged</span>
      </div>

      {/* On a renewal, what he is quoting against. Above the premium field
          because that is the number it is context for. */}
      <IncumbentPanel incumbent={incumbent} />

      {/* WHAT THESE FIELDS ARE NOT SPEAKING FOR. getQuoteDetail collapsed every
          option to the primary, so this form edited one option while silently
          standing in for the quote. On b26d7920 and f8edf033 there is a second
          option IN FRONT OF THE CLIENT that it neither showed nor updated.
          Naming it is the fix here; editing the others is a different piece. */}
      {/* NARROWED 2026-08-07. This box existed because there was no way to see or
          reach the other options; the tab strip is now that, and repeating the
          list under it is the third surface saying one thing, which is what made
          the sheet unusable. What the tabs CANNOT say is that the option you are
          editing is EXCLUDED (a411ea71 carries three, all excluded, so the
          primary chain falls through to list[0] and the wizard edits one), so
          the box survives for exactly that. */}
      {primaryOption?.excluded ? (
        <div className="space-y-1.5 rounded-card border border-warning/40 bg-warning-soft p-2.5 text-[11.5px] text-text">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-warning" />
            <span>
              These fields are <strong>{primaryLabel}</strong>, which is marked excluded.
              It will not be shown to the client.
            </span>
          </div>
          <ul className="ml-5 space-y-0.5 text-[11px] text-text-muted">
            {otherOptions.map((o) => (
              <li key={o.id}>
                {optionName(o)}
                {o.total_premium != null ? ` ${fmtMoney(o.total_premium)}` : ' no premium'}
                {o.term_months ? `, ${o.term_months} mo` : ''}
                {o.presented ? ', presented to the client' : ''}
                {o.excluded ? ', excluded' : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* ONE EDITABLE HOME FOR THE PREMIUM. On a quote whose limits branch,
            each combination is priced hull + the liability leg + the medical
            leg, and this single box speaks for whichever corner is primary.
            Leaving it editable would make it a second writer of a figure the
            axes own: type 9,000 here and the next limit edit puts it back to
            8,932 with nothing saying so. Same treatment the taxes and fees
            fields already get on a fleet, for the same reason. */}
        <div data-field="premium">
        <Field label="Premium" tag={premiumDerived ? 'from the limits' : null}>
          {premiumDerived ? (
            <>
              <div className="rounded-card border border-border bg-bg px-3 py-2.5 text-sm text-text" aria-readonly="true">
                {form.premium ? fmtMoney(toNumber(form.premium)) : 'not answered'}
              </div>
              <Helper>
                This quote offers {cornerCount} combinations, each priced from the hull premium and
                the limits it uses. Change a price on the Coverage page.
              </Helper>
            </>
          ) : (
            <>
              <MoneyInput value={form.premium} onChange={(v) => set({ premium: v })} placeholder="12,345" autoFocus />
              <Helper>Pre-tax, as the carrier quoted it.</Helper>
            </>
          )}
        </Field>
        </div>
        {/* THE TERM LIVES NEXT TO THE PREMIUM IT VARIES. It used to sit at the
            bottom of the rest step, whose hint reads "Everything else. All
            optional." A term is not optional: it decides the policy end date,
            and burying it there is part of why Black Bear Aero's six-month
            policy printed a year. */}
        {/* DEFAULTED TO 12, AND SAYING SO. 361 of 364 option rows are annual,
            so asking every time was asking a question the book had already
            answered. The tag beside the label marks it as the form's answer
            rather than the carrier's until he touches it. */}
        {/* THE SHARED PICKER (decision 14): 12 / 6 / Other with a typed
            month count. The select it replaces offered 'Other' as a value,
            which the server stored as no term at all. */}
        <div data-field="term">
        <Field label="Term" tag={mark('term_months')}>
          <div><TermPicker value={form.term_months} onChange={(v) => set({ term_months: v })} label="" /></div>
          <Helper>The carrier&apos;s policy period. The bind refuses without one.</Helper>
        </Field>
        </div>
        <Field label="Quote number">
          <Input
            value={form.quote_number}
            onChange={(e) => set({ quote_number: e.target.value })}
            placeholder="the carrier's reference"
          />
        </Field>
        {/* PREFILLED ON A RENEWAL from the expiring policy's end date, which is
            the day this one starts. The tag marks it until he confirms it, and
            the helper names WHERE the date came from: a date that appeared by
            itself is the thing that corrupted ten binds, and one that says
            "the expiring policy ends here" can be checked at a glance. */}
        <div data-field="effective_date">
        <Field label="Effective date" tag={mark('proposed_effective')}>
          <DatePicker
            value={form.proposed_effective}
            onChange={(v) => set({ proposed_effective: v })}
            placeholder="Pick the effective date"
          />
          {mark('proposed_effective') && effectiveSource === 'prior_policy_end' ? (
            <Helper>The expiring policy ends this day.</Helper>
          ) : null}
        </Field>
        </div>
      </div>

      {/* COLLAPSED (David, 2026-08-01): "it's just taking up unnecessary space
          and is usually pretty insignificant a metric." 29 of 381 quotes carry
          one. Collapsed is not deleted: a quote that HAS an expiry opens showing
          it, because hiding a stored date behind a closed disclosure is how the
          29 would quietly stop being read. */}
      <OfferExpiry value={form.quote_expires_at} onChange={(v) => set({ quote_expires_at: v })} />

      {/* On a fleet these are the sum of the per-tail figures and cannot be
          typed into, because two editable homes for one number is two answers
          to "what tax is on this policy" (item 8c). A sum of nothing shows
          nothing: no tail stating a tax leaves this blank, never $0. */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="Estimated taxes / surplus lines" tag={derived ? 'from aircraft' : null}>
          {derived ? (
            <div className="rounded-card border border-border bg-bg px-3 py-2.5 text-sm text-text" aria-readonly="true">
              {derived.estimated_taxes == null ? 'not answered' : fmtMoney(derived.estimated_taxes)}
            </div>
          ) : (
            <>
              <MoneyInput value={form.estimated_taxes} onChange={(v) => set({ estimated_taxes: v })} placeholder="0" />
              <Helper>Leave blank when none apply.</Helper>
              {/* The single-aircraft home for the tax suggestion. On a fleet the
                  header is the rows' sum and is not typeable, so the chip lives
                  on each tail instead and this branch never renders. */}
              <TaxChip
                suggestion={taxSuggestionForHeader(taxContext, rows, form)}
                onUse={(amount) => setDefaultedField({ estimated_taxes: String(amount) })}
              />
            </>
          )}
        </Field>
        <Field label="Fees" tag={derived ? 'from aircraft' : null}>
          {derived ? (
            <div className="rounded-card border border-border bg-bg px-3 py-2.5 text-sm text-text" aria-readonly="true">
              {derived.admin_fee == null ? 'not answered' : fmtMoney(derived.admin_fee)}
            </div>
          ) : (
            <MoneyInput value={form.admin_fee} onChange={(v) => set({ admin_fee: v })} placeholder="0" />
          )}
        </Field>
      </div>

      {/* Per-aircraft figures sum to their own totals. The wizard SHOWS the sum
          and offers to take it; it never writes over a number the broker typed,
          because a derived figure quietly replacing an entered one is how a
          policy premium stops matching the quote it came from. */}
      {/* Only the PREMIUM is offered now. Taxes and fees on a fleet are already
          the rows' sum above, so offering to copy them would be offering to
          copy a number into itself. */}
      {/* NOT OFFERED WHERE IT WOULD BE IGNORED. On a grid the premium is derived
          from the limits and this step no longer writes it, so a chip that fills
          the box would look like it did something and change nothing. */}
      {hasPerTail && perTail.premium > 0 && !premiumDerived ? (
        <div className="flex flex-wrap items-center gap-2 rounded-card border border-border bg-bg/40 px-3 py-2 text-[11.5px] text-text-muted">
          <span>
            Per aircraft sums to <strong className="text-text">{fmtMoney(perTail.premium)}</strong> premium.
          </span>
          <button
            type="button"
            onClick={() => set({ premium: String(perTail.premium) })}
            className="rounded-pill border border-border bg-bg px-2.5 py-0.5 text-[11px] text-text hover:border-border-strong"
          >
            Use it
          </button>
        </div>
      ) : null}

      {/* THE SIX-MONTH OFFER IS A SECOND PREMIUM (David: "the 6 month thing
          should just be 2 premiums"), not a cloned quote and certainly not a
          cloned scenario. It becomes a second OPTION under this quote,
          inheriting this option's coverage through term_variant_of so the two
          are structurally incapable of disagreeing on hull, limits,
          deductibles or schedule. Black Bear Aero's pair disagreed by $5,000
          of hull because a human retyped it. */}
      {/* The limits are edited on the Coverage page now, as two lists with a
          `+` each, and the combinations are generated from them. There is no
          add-an-option form here any more. */}
      <SixMonthOffer
        form={form}
        options={allOptions}
        onAdd={onAddSixMonth}
        onRemove={onRemoveSixMonth}
        busy={optionBusy}
        offersShortTerm={form.carrier_offers_short_term === true}
      />

      {/* THE LEGS MOVED TO COVERAGE (2026-08-07). David: "the hull, liability,
          and medical payments premium should probably just move to the coverage
          page. and set each of those against the limits that they represent."
          A premium is meaningless beside nothing; beside the limit it buys it
          reads as a price. They are still OPTIONAL on a single package, which
          is the point of "line iteming isn't necessary until there are
          choices" — the ability to break one out never goes away, it just
          stops being demanded. The total stays here, on the offer. */}
      {/* THE FEE IS NOT HERE ON PURPOSE, and it already has a home: the "Fees"
          field beside Taxes below, which reads admin_fee and takes the
          per-aircraft derived sum when there is one. A fee is payable by the
          client and earns the agency nothing, so it is not premium and must
          never be totalled as premium — which is exactly why it stays out of
          this breakdown rather than getting a second input here. */}

      {delta ? (
        <div className={cn(
          'flex items-center gap-2 rounded-card border px-3 py-2 text-[11px]',
          delta.kind === 'warn' ? 'border-warning/50 bg-warning-soft text-warning' : 'border-success/30 bg-success-soft text-success'
        )}>
          {delta.kind === 'warn' ? <AlertTriangle size={14} /> : null}
          <span>{delta.message}</span>
        </div>
      ) : null}
    </div>
  );
}

// A SURFACE THAT REPORTS A BLOCKER MUST OFFER THE DOOR THROUGH IT. Removing a
// limit removes the combinations that used it, and some of those may be in front
// of the client. The server refuses and names them; this is where he says yes.
//
// It is a BANNER, not a browser dialog: no alert(), no confirm(), and the rows
// it is about stay on screen behind it (the standing rule in CLAUDE.md, and the
// reason is that a modal hides the very numbers the decision is about).
const DEFAULT_ASK_LABELS = {
  term_months: 'Term', proposed_effective: 'Effective date', coverage_type: 'Coverage type',
  liability_per_occurrence: 'Per occurrence', liability_per_passenger: 'Per passenger',
  medical_per_person: 'Medical per person', opw_certificate: 'Open pilot certificate',
};
const DEFAULT_ASK_MONEY = new Set(['liability_per_occurrence', 'liability_per_passenger', 'medical_per_person']);

// The step is being left with defaults he never touched. Same banner shape as
// WithdrawAsk below: inline, never a browser dialog.
function DefaultsAsk({ ask, form, onAnswer, busy }) {
  if (!ask || !form) return null;
  const limitsOnly = ask.keys.every((k) => DEFAULT_ASK_MONEY.has(k));
  const shown = (k) => {
    if (k === 'term_months') return `${form[k]} months`;
    return DEFAULT_ASK_MONEY.has(k) ? fmtMoney(toNumber(form[k])) : String(form[k]);
  };
  return (
    <div className="mb-3 space-y-2 rounded-card border border-warning/50 bg-warning-soft px-3 py-2.5">
      <div className="flex items-start gap-2 text-[11.5px] text-text">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-warning" />
        <span>
          <span className="block font-semibold">{limitsOnly ? 'Confirm default limits?' : 'Confirm defaults?'}</span>
          <span className="mt-1 block text-text-muted">
            {ask.keys.map((k) => `${DEFAULT_ASK_LABELS[k] || k} ${shown(k)}`).join(' · ')}
          </span>
          <span className="mt-1 block text-[10.5px] text-text-dim">
            Unconfirmed, they are not saved and the quote lists them as not stated.
          </span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button" onClick={() => onAnswer(true)} disabled={busy}
          className="rounded-pill border border-accent bg-accent px-3 py-1 text-[11px] font-medium text-white disabled:opacity-40"
        >
          Confirm and continue
        </button>
        <button type="button" onClick={() => onAnswer(false)} disabled={busy} className="text-[11px] text-text-dim hover:text-text-muted">
          Leave them unstated
        </button>
      </div>
    </div>
  );
}

function WithdrawAsk({ ask, onConfirm, onCancel, busy }) {
  if (!ask) return null;
  return (
    <div className="mb-3 space-y-2 rounded-card border border-warning/50 bg-warning-soft px-3 py-2.5">
      <div className="flex items-start gap-2 text-[11.5px] text-text">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-warning" />
        <span>
          <span className="block font-semibold">{ask.message}</span>
          {ask.rows.length ? (
            <span className="mt-1 block text-text-muted">
              {ask.rows.map((r) => r.label).join(' · ')}
            </span>
          ) : null}
          <span className="mt-1 block text-[10.5px] text-text-dim">
            They stop being visible to the client immediately. Nothing else on the quote changes.
          </span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button" onClick={onConfirm} disabled={busy}
          className="rounded-pill border border-warning bg-warning px-3 py-1 text-[11px] font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Removing…' : 'Remove them and save'}
        </button>
        <button type="button" onClick={onCancel} className="text-[11px] text-text-dim hover:text-text-muted">
          Keep them: put the limit back
        </button>
      </div>
    </div>
  );
}

// One liability limit and what it costs: the pair in column A, the price in
// column B. `index > 0` rows are the ones the `+` added and carry a remove.
function LiabilityLegFields({ index, leg, showTria, mark, implied, onChange, onRemove }) {
  return (
    <>
      {/* THE LEG'S HEADING IS ITS OWN FULL-WIDTH ROW, and that is the whole
          alignment fix (David, 2026-08-07: "the alignment is off"). It sat
          INSIDE column A, so column A began one heading lower than column B and
          the second limit's price rendered beside the words "Liability limit 2"
          instead of beside the limit it prices. The premium being level with its
          limit is the entire argument of this layout, so the first row of every
          block has to start at the same height in both columns. */}
      {index > 0 ? (
        <div className="flex items-center justify-between sm:col-span-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            Liability limit {index + 1}
          </span>
          {onRemove ? <RemoveLeg onClick={onRemove} what="limit" /> : null}
        </div>
      ) : null}
      {/* SMOOTH OR SPLIT IS THIS LEG'S ANSWER, not the section's (David,
          2026-09-09): a quote may offer $1M CSL beside $1M/$100K, and the
          toggle above the list could not say so. Neither side is lit until the
          broker says which it is. A default here would be an answer nobody
          gave, and it rides all the way to paper. */}
      <div className="sm:col-span-2">
        <div className="inline-flex gap-1 rounded-pill bg-bg p-0.5">
          {[['smooth', 'Smooth (CSL)'], ['split', 'Split']].map(([mode, label]) => {
            const on = leg.smooth === (mode === 'smooth');
            return (
              <button
                key={mode}
                type="button"
                onClick={() => onChange({ smooth: mode === 'smooth' })}
                className={cn(
                  'rounded-pill px-4 py-1.5 text-xs font-medium uppercase tracking-wide',
                  on ? 'bg-accent text-white' : 'text-text-muted hover:text-text'
                )}
              >
                {label}
              </button>
            );
          })}
        </div>
        {leg.smooth === null ? <Helper>Not answered yet.</Helper> : null}
      </div>
      <div className="space-y-3">
        <Field label={leg.smooth ? 'Per occurrence (CSL)' : 'Per occurrence'} tag={mark('liability_per_occurrence')}>
          <MoneyInput value={leg.per_occurrence} onChange={(v) => onChange({ per_occurrence: v })} placeholder="1,000,000" />
          <QuickPicks values={LIAB_OCC_QUICK} onPick={(v) => onChange({ per_occurrence: String(v) })} />
        </Field>
        {leg.smooth === true ? null : (
          <Field label="Per passenger" tag={mark('liability_per_passenger')}>
            <MoneyInput value={leg.per_passenger} onChange={(v) => onChange({ per_passenger: v })} placeholder="100,000" />
            <QuickPicks values={LIAB_PAX_QUICK} onPick={(v) => onChange({ per_passenger: String(v) })} />
          </Field>
        )}
      </div>
      <div className="space-y-3 sm:w-44">
        <Field label="Premium" hint={index > 0 ? 'required' : 'optional'}>
          <MoneyInput value={leg.premium} onChange={(v) => onChange({ premium: v })} placeholder="what this limit costs" />
          <ImpliedChip value={implied} onUse={(v) => onChange({ premium: String(v) })} />
        </Field>
        {/* TRIA'S AMOUNT RIDES THE LIABILITY LEG, and only once the leg branches.
            It is a per-option column and always has been; what the grid forced
            was a statement of WHICH axis it travels with, and the book answers
            it: Allianz priced 675 against $20M and 840 against $25M. One field
            for the whole quote would have flattened those two into one on the
            first save. With a single limit nothing moves and the amount stays
            in the TRIA block at the foot of the page, where it has always been. */}
        {showTria ? (
          <Field label="TRIA premium">
            <MoneyInput value={leg.tria_premium} onChange={(v) => onChange({ tria_premium: v })} placeholder="275" />
          </Field>
        ) : null}
      </div>
    </>
  );
}

function MedicalLegFields({ index, leg, mark, implied, onChange, onRemove }) {
  return (
    <>
      {/* Same full-width heading row as the liability leg above, for the same
          reason: inside column A it pushed the limit one heading below its own
          price. */}
      {index > 0 ? (
        <div className="flex items-center justify-between sm:col-span-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            Medical payments {index + 1}
          </span>
          {onRemove ? <RemoveLeg onClick={onRemove} what="limit" /> : null}
        </div>
      ) : null}
      <div className="space-y-1">
        <Field label="Medical payments per person" tag={mark('medical_per_person')}>
          <MoneyInput value={leg.per_person} onChange={(v) => onChange({ per_person: v })} placeholder="5,000" />
          <QuickPicks values={MEDICAL_QUICK} onPick={(v) => onChange({ per_person: String(v) })} />
        </Field>
      </div>
      <div className="sm:w-44">
        <Field label="Premium" hint={index > 0 ? 'required' : 'optional'}>
          <MoneyInput value={leg.premium} onChange={(v) => onChange({ premium: v })} placeholder="what this limit costs" />
          <ImpliedChip value={implied} onUse={(v) => onChange({ premium: String(v) })} />
        </Field>
      </div>
    </>
  );
}

function RemoveLeg({ onClick, what }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-[11px] text-text-dim hover:text-danger"
      title={`Remove this ${what} and the options that use it`}
    >
      <Trash2 size={11} /> Remove
    </button>
  );
}

// The `+`. Spans both columns so it reads as belonging to the section above it
// rather than to either column, which is what "squeezes it in between the
// original and med pay" means on the page.
function AddAxisRow({ label, hint, onClick }) {
  return (
    <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 rounded-pill border border-accent bg-accent-soft px-2.5 py-1 text-[11px] font-medium text-accent"
      >
        <Plus size={12} /> {label}
      </button>
      {hint ? <span className="text-[11px] text-text-dim">{hint}</span> : null}
    </div>
  );
}

// A premium the STORED TOTALS imply for a leg that records none of its own.
// Offered, never written, exactly like the tax chip: Milam's Mach 2 records
// medical_premium on one of its four corners, and the other three totals say
// $120 for the $10,000 row three times over. A figure four rows agree on is
// worth showing; writing it on his behalf is not.
function ImpliedChip({ value, onUse }) {
  if (value == null) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10.5px] text-text-muted">
      <span>The prices already recorded imply <strong className="text-text">{fmtMoney(value)}</strong>.</span>
      <button
        type="button"
        onClick={() => onUse(value)}
        className="rounded-pill border border-border bg-bg px-2 py-0.5 text-[10.5px] text-text hover:border-border-strong"
      >
        Use it
      </button>
    </div>
  );
}

// EVERY COMBINATION, PRICED. The Tower lets the client move one limit
// independently of the other, and that is only safe when every corner exists as
// a stored option; generating them satisfies it by construction. This is the
// broker's sight of what he is about to store, and it is prices rather than rows
// because the rows are not his to manage.
function CornerPreview({ corners, hullPremium, blockers }) {
  if (!corners || corners.length <= 1) return null;
  if (blockers.length) {
    return (
      <div className="mt-3 flex items-start gap-2 rounded-card border border-warning/40 bg-warning-soft px-3 py-2 text-[11.5px] text-text">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-warning" />
        <span>
          <span className="block font-semibold">
            {corners.length} combinations, and they cannot all be priced yet.
          </span>
          <span className="block text-text-muted">Still needed: {blockers.join('; ')}.</span>
          <span className="mt-1 block text-[10.5px] text-text-dim">
            A single package needs no breakdown. A choice between two does, because the client can
            pick either one and both have to carry a price the carrier gave.
          </span>
        </span>
      </div>
    );
  }
  // COLLAPSED (David, 2026-08-07: "the '4 options generated on save' card is
  // valuable... collapsed"). The prices are worth a look and are not worth the
  // height of four rows on a page whose subject is the limits. The SUMMARY LINE
  // carries the count and the range, so closed it still answers "how many, and
  // roughly what" without being opened.
  const totals = corners
    .map((c) => cornerTotal({ hull_premium: hullPremium, liability: c.liability, medical: c.medical }))
    .filter((v) => v != null);
  const range = totals.length
    ? (Math.min(...totals) === Math.max(...totals)
      ? fmtMoney(totals[0])
      : `${fmtMoney(Math.min(...totals))} to ${fmtMoney(Math.max(...totals))}`)
    : '';
  return (
    <details className="mt-3 rounded-card border border-border bg-bg/40 px-3 py-2">
      <summary className="cursor-pointer text-[11.5px] text-text-muted">
        {corners.length} options, generated on save
        {range ? <span className="ml-2 text-text-dim">{range}</span> : null}
      </summary>
      <div className="mt-1.5 space-y-0.5">
        {corners.map((c, i) => (
          <div key={i} className="flex flex-wrap items-baseline gap-x-2 text-[11.5px]">
            <span className="text-text">{describeLiability(c.liability)}</span>
            <span className="text-text-dim">·</span>
            <span className="text-text-muted">{describeMedical(c.medical)} med</span>
            <span className="ml-auto font-medium text-text">
              {fmtMoney(cornerTotal({ hull_premium: hullPremium, liability: c.liability, medical: c.medical }))}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-1.5 text-[10.5px] text-text-dim">
        Hull plus the limit plus the medical row, each one. New combinations are not shown to the
        client until you check them on the present sheet.
      </p>
    </details>
  );
}

// ── THE AXES, LEG 0 AND THE EXTRAS ────────────────────────────────────────
// Leg 0 IS the scalar fields the form has always carried; legs 2..N live in
// `*_legs_extra` (quoteWizardModel explains why, and it is one home per leg
// rather than two homes for one leg). These four helpers are the only place
// that seam is visible: everything above them sees one list.
// A new leg starts UNANSWERED on smooth/split, nothing copied from the leg
// above it (David, 2026-09-09: "fork can be blank ... no copy").
const BLANK_LIAB = { per_occurrence: '', per_passenger: '', premium: '', tria_premium: '', smooth: null, key: null };
const BLANK_MED = { per_person: '', premium: '', key: null };

// Writing leg 0 writes the scalars; writing leg i writes the array.
function liabPatchFor(form, i, patch) {
  if (i > 0) {
    const extras = [...(form.liability_legs_extra || [])];
    extras[i - 1] = { ...extras[i - 1], ...patch };
    return { liability_legs_extra: extras };
  }
  const out = {};
  if ('per_occurrence' in patch) out.liability_per_occurrence = patch.per_occurrence;
  if ('per_passenger' in patch) out.liability_per_passenger = patch.per_passenger;
  if ('smooth' in patch) out.liability_smooth = patch.smooth;
  if ('premium' in patch) out.liability_premium = patch.premium;
  if ('tria_premium' in patch) out.tria_premium = patch.tria_premium;
  return out;
}
function medPatchFor(form, i, patch) {
  if (i > 0) {
    const extras = [...(form.medical_legs_extra || [])];
    extras[i - 1] = { ...extras[i - 1], ...patch };
    return { medical_legs_extra: extras };
  }
  const out = {};
  if ('per_person' in patch) out.medical_per_person = patch.per_person;
  if ('premium' in patch) out.medical_premium = patch.premium;
  return out;
}
// REMOVING LEG 0 IS PROMOTION, NOT A HOLE. Without this the FIRST limit is the
// one limit that cannot be dropped, which is the sort of asymmetry a broker
// discovers at the worst moment. The next leg moves into the scalars, carrying
// its loaded key so the server still matches it to its own rows.
function liabRemovePatch(form, i) {
  const extras = [...(form.liability_legs_extra || [])];
  if (i > 0) { extras.splice(i - 1, 1); return { liability_legs_extra: extras }; }
  const next = extras.shift() || BLANK_LIAB;
  return {
    liability_per_occurrence: next.per_occurrence, liability_per_passenger: next.per_passenger,
    liability_premium: next.premium, tria_premium: next.tria_premium,
    // The promoted leg keeps ITS structure. Without this, deleting leg 0 left
    // the old smooth/split on the survivor.
    liability_smooth: next.smooth ?? null,
    liability_leg_key: next.key || null, liability_legs_extra: extras,
  };
}
function medRemovePatch(form, i) {
  const extras = [...(form.medical_legs_extra || [])];
  if (i > 0) { extras.splice(i - 1, 1); return { medical_legs_extra: extras }; }
  const next = extras.shift() || BLANK_MED;
  return {
    medical_per_person: next.per_person, medical_premium: next.premium,
    medical_leg_key: next.key || null, medical_legs_extra: extras,
  };
}

function TermsStep({ form, set, rows, setRow, setRows, defaulted, taxContext, setRowDefaulted }) {
  // One word beside the label of anything the form filled in. It disappears the
  // moment he edits that field (set() clears the key), so what is left marked is
  // exactly what nobody has confirmed.
  const mark = (k) => (defaulted?.has(k) ? 'default' : null);
  const markRow = (i, k) => (defaulted?.has(rowKey(i, k)) ? 'default' : null);

  // The two lists, assembled through the model's accessors so the shape the
  // screen renders and the shape the save sends are the same one.
  // FALSE means the stored options are separate offers rather than points on a
  // grid, so the tab strip is the editor and the `+` has nothing to write to.
  const axesOk = form.axes_ok !== false;
  const liabLegs = liabilityLegsOf(form);
  const medLegs = medicalLegsOf(form);
  const corners = buildCorners(liabLegs, medLegs);
  const blockers = pricingBlockers({
    hull_premium: form.hull_premium, liability: liabLegs, medical: medLegs, tria_status: form.tria_status,
  });
  const onLiabChange = (i, patch) => set(liabPatchFor(form, i, withImpliedSplit(liabLegs[i], patch)));
  const onMedChange = (i, patch) => set(medPatchFor(form, i, patch));
  const onLiabAdd = () => set({ liability_legs_extra: [...(form.liability_legs_extra || []), { ...BLANK_LIAB }] });
  const onMedAdd = () => set({ medical_legs_extra: [...(form.medical_legs_extra || []), { ...BLANK_MED }] });
  const onLiabRemove = (i) => set(liabRemovePatch(form, i));
  const onMedRemove = (i) => set(medRemovePatch(form, i));

  return (
    <div className="space-y-4">
      <fieldset className="rounded-card border border-border bg-bg/30 p-3" data-field="liability_limit options">
        <legend className="px-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">Liability</legend>
        {/* The smooth/split pills ride each leg (LiabilityLegFields) since
            2026-09-09: a quote may offer $1M CSL beside $1M/$100K, so the
            structure is not one answer for the section. */}
        {/* LIMITS IN COLUMN A, THE PREMIUM THEY BUY IN COLUMN B (David's layout,
            2026-08-07). Per occurrence and per passenger STACK, because they are
            one choice the carrier rated as a pair, and the single premium beside
            them is what that pair costs. Med pay is its own row underneath with
            its own premium, because it is a separate choice.

            The alignment is the argument: a premium in its own section reads as
            a share of the total, and a premium beside a limit reads as the price
            of that limit. Same numbers, different claim.

            AND EACH SECTION IS A LIST. David: "if there's a + it's for the Liabs
            section and it replicates it/squeezes it in between the original and
            med pay; same for med pay: another + and it appends the next option
            to the bottom there." What gets STORED is still one quote_options row
            per combination, generated on save; the broker never manages them. */}
        <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-[1fr_auto] sm:items-start">
          {liabLegs.map((leg, i) => (
            <LiabilityLegFields
              key={i}
              index={i}
              leg={leg}
              triaOptional={form.tria_status === 'optional'}
              showTria={liabLegs.length > 1 && form.tria_status === 'optional'}
              mark={i === 0 ? mark : () => null}
              implied={form.axes_implied?.[leg.key]}
              onChange={(patch) => onLiabChange(i, patch)}
              onRemove={liabLegs.length > 1 ? () => onLiabRemove(i) : null}
            />
          ))}
          {/* A CONTROL THAT WOULD SAVE NOTHING IS NOT RENDERED. On an option set
              that is not a grid the save takes the single-option path, so a `+`
              here would append a limit that is silently dropped — a field that
              saves nothing, which is the inverse of the bug that wiped the $60
              med-pay premium and just as quiet. The tab strip above already says
              why; this says what follows from it. */}
          {axesOk ? (
            <AddAxisRow
              label="Add a liability limit"
              hint={liabLegs.length > 1
                ? `${liabLegs.length} liability limits x ${medLegs.length} medical row${medLegs.length === 1 ? '' : 's'} = ${liabLegs.length * medLegs.length} options`
                : 'A second limit the client can choose between.'}
              onClick={onLiabAdd}
            />
          ) : (
            <p className="text-[11px] text-text-dim sm:col-span-2">
              These limits belong to the option open above. A second limit cannot be added as a
              choice here, because these options differ in more than their limits.
            </p>
          )}

          <div className="border-t border-border/60 pt-3 sm:col-span-2" />

          {medLegs.map((leg, i) => (
            <MedicalLegFields
              key={i}
              index={i}
              leg={leg}
              mark={i === 0 ? mark : () => null}
              implied={form.axes_implied?.[leg.key]}
              onChange={(patch) => onMedChange(i, patch)}
              onRemove={medLegs.length > 1 ? () => onMedRemove(i) : null}
            />
          ))}
          {axesOk ? <AddAxisRow label="Add a medical payments limit" onClick={onMedAdd} /> : null}
        </div>

        {/* WHAT THE TWO LISTS PRODUCE. He never manages the corners, and he does
            see their prices: a combination the client can toggle to is a price
            somebody will be quoted, and the whole reason for generating them is
            that no toggle can reach one nobody stated. */}
        <CornerPreview
          corners={corners}
          hullPremium={form.hull_premium}
          blockers={blockers}
        />
      </fieldset>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div data-field="coverage_type">
        <Field label="Coverage type" tag={mark('coverage_type')}>
          {/* The quote's own stored value rides the list even when it is not
              one we offer, so a historical 'Full Flight (Ground & Flight)'
              stays selected and visible instead of rendering the select blank
              on a quote that has an answer (item 4, 2026-07-30). */}
          <select value={form.coverage_type} onChange={(e) => set({ coverage_type: e.target.value })} className={inputClass}>
            <option value="">— not answered —</option>
            {coverageTypeOptions(form.coverage_type).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Helper>{rows.length >= 2 ? 'The default for every aircraft; override per aircraft below.' : 'Hull-coverage scope for this policy.'}</Helper>
        </Field>
        </div>
        <Field label="Aircraft use" tag={mark('aircraft_usage')}>
          <select value={form.aircraft_usage} onChange={(e) => set({ aircraft_usage: e.target.value })} className={inputClass}>
            <option value="">— not answered —</option>
            {AIRCRAFT_USE_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <div data-field="deductible_not_in_motion">
        <Field label="Not-in-motion deductible">
          <Input value={form.non_motion_deductible} onChange={(e) => set({ non_motion_deductible: e.target.value })} placeholder="5% or 500" />
          <Helper>{deductiblePreview(form.non_motion_deductible) || '5% of hull, or a flat $ amount.'}</Helper>
        </Field>
        </div>
        <div data-field="deductible_in_motion">
        <Field label="In-motion deductible">
          <Input value={form.in_motion_deductible} onChange={(e) => set({ in_motion_deductible: e.target.value })} placeholder="5% or 500" />
          <Helper>{deductiblePreview(form.in_motion_deductible) || '5% of hull, or a flat $ amount.'}</Helper>
        </Field>
        </div>
        {/* "(default US)" is gone. Nothing anywhere honoured it, and it was
            wrong about the shape of the data as well as its value: every real
            territory is a paragraph naming countries and exclusions, and the
            thirteen carriers with no standard wording have no default at all
            (wave 2 item 5). Blank now means blank. */}
        {/* SPANS BOTH COLUMNS. Every real territory is a paragraph naming
            countries and exclusions, and half a row was never enough of it to
            read without scrolling a textarea. */}


      {/* HULL MIRRORS THE SAME SHAPE: what is covered on the left, what it costs
          on the right. The deductibles sit with it because they are part of the
          hull answer, not a separate subject. */}
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-[1fr_auto] sm:items-start">
        {/* NO HULL VALUE FIELD HERE. I added one an hour ago and it was the
            second place on this page showing the same number: the aircraft card
            below already carries it, and it is SET there. Two renderings of one
            value is how they start disagreeing, and this one could not even be
            edited. The premium is what this section adds. */}
        <div className="sm:w-44">
          <Field label="Hull premium" hint="optional">
            <MoneyInput value={form.hull_premium} onChange={(v) => set({ hull_premium: v })} placeholder="what the hull costs" />
          </Field>
        </div>
      </div>
      </div>

      {/* The reversed-deductible catch, unchanged from QuoteEntrySheet: a
          not-in-motion figure above the in-motion one is almost always the two
          typed into the wrong boxes. It offers a swap, it does not perform one. */}
      {(() => {
        const im = parseDeductible(form.in_motion_deductible);
        const nim = parseDeductible(form.non_motion_deductible);
        if (!im || !nim || im.kind !== nim.kind || nim.value <= im.value) return null;
        return (
          <div className="flex items-center gap-2 rounded-card border border-warning/40 bg-warning-soft p-2 text-xs text-text">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 text-warning" />
            <span className="flex-1">Not-in-motion is the higher of the two, which is usually reversed.</span>
            <button
              type="button"
              onClick={() => set({
                in_motion_deductible: form.non_motion_deductible,
                non_motion_deductible: form.in_motion_deductible,
              })}
              className="rounded-card border border-warning/60 px-2 py-0.5 text-[11px] font-semibold text-warning hover:bg-warning/10"
            >
              Swap
            </button>
          </div>
        );
      })()}



      <div data-field="hull_value">
      {rows.length === 0 ? (
        <div className="rounded-card border border-border bg-bg/30 p-3">
          <Field label="Hull value">
            <div className="rounded-card border border-border bg-bg px-3 py-2.5 text-sm text-text">
              {form.hull_value ? fmtMoney(toNumber(form.hull_value)) : 'not answered'}
            </div>
          </Field>
          <Helper>No aircraft lines on this quote, so the hull value is read-only here.</Helper>
        </div>
      ) : rows.length === 1 ? (
        /* ONE AIRCRAFT GETS NO SUB-FORM (item 8b). David: "it's also confusing
           to have the deductibles, taxes, and fees listed when there's only one
           aircraft." Every one of those is already asked once above or on the
           offer step, and asking again per tail is asking twice. Only the two
           questions with no header equivalent stay: the hull value, which is
           per-tail and asked nowhere else, and the training requirement. */
        <SingleAircraftFields row={rows[0]} setRow={setRow} />
      ) : (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Per aircraft</p>
          {rows.map((r, i) => (
            <div key={r.tail_number_id || i} className="rounded-card border border-border bg-bg/30 p-3">
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold text-text">{r.tail_display || `Aircraft ${i + 1}`}</span>
                <span className="text-[10px] text-text-dim">{[r.year, r.make_model].filter(Boolean).join(' ')}</span>
              </div>
              {/* OFF THE FLEET (Docjosh, 2026-09-09). The roster door retired
                  this aircraft from the deal; the quote still carries its line.
                  Say so where the line is, and offer the one honest answer for
                  a fleet quote where one aircraft leaves: drop that line. The
                  next save sends the shorter schedule and the server overwrites
                  the stored one. === true on purpose: null means the live
                  fleet could not be read, and unknown is not "gone". */}
              {r.off_fleet === true ? (
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-card border border-warning/40 bg-warning-soft px-2.5 py-1.5 text-[11px] text-warning">
                  <span>
                    No longer on the fleet{r.retired_reason ? ` (${r.retired_reason})` : ''}. This line still counts on the quote until it is removed.
                  </span>
                  <button
                    type="button"
                    onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                    className="rounded-pill border border-warning/60 bg-bg px-2.5 py-0.5 text-[11px] font-medium text-warning hover:bg-warning/10"
                  >
                    Remove from this quote
                  </button>
                </div>
              ) : null}
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3">
                <Field label="Hull value">
                  <MoneyInput value={r.hull_value} onChange={(v) => setRow(i, { hull_value: v })} placeholder="85,000" />
                </Field>
                {rows.length >= 2 ? (
                  <Field label="Coverage type (override)">
                    <select value={r.coverage_type} onChange={(e) => setRow(i, { coverage_type: e.target.value })} className={inputClass}>
                      <option value="">inherit ({form.coverage_type || 'not answered'})</option>
                      {coverageTypeOptions(r.coverage_type).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                ) : null}
                <Field label="Aircraft premium">
                  <MoneyInput value={r.aircraft_premium} onChange={(v) => setRow(i, { aircraft_premium: v })} placeholder="per-aircraft" />
                </Field>
                <Field label="Not-in-motion ded">
                  <Input value={r.deductible_not_in_motion} onChange={(e) => setRow(i, { deductible_not_in_motion: e.target.value })} placeholder={form.non_motion_deductible ? `blank = header (${form.non_motion_deductible})` : '500'} />
                </Field>
                <Field label="In-motion ded">
                  <Input value={r.deductible_in_motion} onChange={(e) => setRow(i, { deductible_in_motion: e.target.value })} placeholder={form.in_motion_deductible ? `blank = header (${form.in_motion_deductible})` : '5% or 500'} />
                </Field>
                {/* PER TAIL, because a fleet spanning two states is a real
                    case and the rate that applies is the one where THAT
                    aircraft is based. The offer step sums these rows exactly as
                    it already does (item 8c); nothing here sums anything. */}
                <Field label="State taxes" tag={markRow(i, 'estimated_taxes')}>
                  <MoneyInput value={r.estimated_taxes} onChange={(v) => setRow(i, { estimated_taxes: v })} placeholder="0" />
                  <TaxChip
                    suggestion={taxSuggestionForTail(taxContext, r)}
                    onUse={(amount) => setRowDefaulted(i, { estimated_taxes: String(amount) })}
                  />
                </Field>
                <Field label="Fees">
                  <MoneyInput value={r.fees} onChange={(v) => setRow(i, { fees: v })} placeholder="0" />
                </Field>
                {/* Training checkbox removed (David 2026-08-12): a training
                    requirement is the carrier's answer and lives in
                    Pilot_Requirements. This one's own comment claimed FAA
                    seeding that never existed; it seeded from a column no
                    reader consumed. Stored values pass through untouched. */}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* TRIA, AT THE FOOT OF THE COVERAGE IT IS PART OF (David, 2026-08-01:
          it "does not warrant its own page"). One question, three buttons, and
          a page turn for it cost more than it bought. It is still REQUIRED by
          requiredFor('terms') and its answer still saves through triaBody, so
          what moved is where it is asked and nothing about whether it is. */}
      <fieldset className="rounded-card border border-border bg-bg/30 p-3" data-field="tria_premium">
        <legend className="px-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
          Terrorism (TRIA)
        </legend>
        <TriaStep form={form} set={set} perLeg={liabLegs.length > 1} />
      </fieldset>

      {/* TERRITORY SAT IN THE MIDDLE OF THE HULL BLOCK. David: "the territory is
          taking up space in the middle for something that practically never
          changes." It is a paragraph of carrier boilerplate that is right by
          default and edited almost never, and it was separating the hull answer
          from its own deductibles. Collapsed to a one-line summary at the foot
          of the step, openable when it does need changing. Not removed: a
          control nobody can reach is a coverage nobody can sell.

          AND IT IS AT THE ACTUAL FOOT NOW (David, 2026-08-07: "territory can
          still migrate down to the bottom of that page. wasting space anywhere
          else"). "Collapsed" moved the paragraph and left the closed row
          sitting between the deductibles and the aircraft card, which is
          still the middle. A control that practically never changes belongs
          past everything that does. */}
      <details className="rounded-card border border-border bg-bg/30 px-3 py-2">
        <summary className="cursor-pointer text-[11.5px] text-text-muted">
          Territory
          <span className="ml-2 text-text-dim">
            {form.territory ? `${String(form.territory).slice(0, 60)}${String(form.territory).length > 60 ? '…' : ''}` : 'not stated'}
          </span>
        </summary>
        <div className="mt-2">
          <Field label="Territory" tag={mark('territory')}>
            <AutoTextarea
              value={form.territory}
              onChange={(v) => set({ territory: v })}
              placeholder="The carrier's territory wording"
            />
          </Field>
        </div>
      </details>
    </div>
  );
}

// The single-aircraft shape of the block above: the two questions that have no
// header equivalent, and nothing else (item 8b).
//
// THE OVERRIDE NOTE IS THE LOAD-BEARING PART. Collapsing the card hides four
// per-tail controls, and a value stored in any of them BEATS the header field
// he is looking at. Hiding a control is fine; hiding a value that overrules the
// one on screen is how a deductible he cannot see ends up on a binder. So a row
// carrying any of them says so, and offers to drop them.
function SingleAircraftFields({ row, setRow }) {
  // ── THE DEDUCTIBLE BANNER IS DELETED (RULING 1, 2026-08-07) ─────────────
  //
  // David: "Why does that warning exist? What are we warning about? What's that
  // got to do with the price of tea in China?" Nothing, and that is the correct
  // read.
  //
  // It was never about insurance. The deductible is stored TWICE on one option —
  // the scalar column and aircraft_coverage[n] inside the same row — and nothing
  // kept them in step, so somebody added a banner that fires when they disagree.
  // It compared a row to ITSELF and called one of the two columns "the aircraft
  // line". The $27,500 it kept showing Milam was the EXPIRING policy's, seeded
  // into the JSONB and never moved.
  //
  //   A surface that reports an internal inconsistency to a user has escalated a
  //   storage bug into a business decision. The fix is one home, not clearer
  //   wording.
  //
  // Four rewrites went into that sentence across three prompts. The cause is now
  // removed at the writer (mapBodyToOption -> applyDeductiblesToSchedule), so
  // there is nothing left for it to report, and rewording it a fifth time would
  // have been the wrong repair a fifth time.
  //
  // "Match the quote" goes with it. It only ever cleared the JSONB copy — it
  // could not reach policy_aircraft, so it was misnamed rather than dangerous —
  // and the writer fix removes its reason to exist.
  return (
    <div className="space-y-2 rounded-card border border-border bg-bg/30 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-text">{row.tail_display || 'This aircraft'}</span>
        <span className="text-[10px] text-text-dim">{[row.year, row.make_model].filter(Boolean).join(' ')}</span>
      </div>
      {/* The only aircraft on the quote has left the fleet. There is no line to
          fall back to, so this is not a schedule edit: the quote is retired
          from the ladder, never emptied here. Notice only, no control. */}
      {row.off_fleet === true ? (
        <div className="rounded-card border border-warning/40 bg-warning-soft px-2.5 py-1.5 text-[11px] text-warning">
          No longer on the fleet{row.retired_reason ? ` (${row.retired_reason})` : ''}. It is the only aircraft on this quote, so retire the quote from the ladder rather than editing it here.
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label="Hull value">
          <MoneyInput value={row.hull_value} onChange={(v) => setRow(0, { hull_value: v })} placeholder="85,000" />
        </Field>
        {/* Training checkbox removed (David 2026-08-12) — see the multi-
            aircraft schedule note. */}
      </div>
    </div>
  );
}


// TRIA is the one real per-placement variable on a P&B book (QUOTE-WIZARD-SPEC
// section 0), and the only question here that can be got wrong in a way a client
// signs.
//
// FOUR states as of 2026-07-30, and "not answered" is still one of them. David:
// "many P&B policies don't include TRIA. some do at additional premium. some
// will add it optionally." The old two-button toggle could not record a policy
// that simply excludes terrorism cover, so the commonest real answer had nowhere
// to go and looked identical to not having asked. Three explicit choices now,
// and this step still never fills itself in: absence in a document is not
// evidence of anything, and 'excluded' is an answer somebody has to give.
const TRIA_CHOICES = [
  ['included', 'Included in the premium', 'Built in. No separate charge.'],
  ['optional', 'Optional, at a price', 'The client elects it. A premium is required.'],
  ['excluded', 'Not offered', 'The carrier does not write it on this policy.'],
];

function TriaStep({ form, set, perLeg }) {
  const status = form.tria_status;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {TRIA_CHOICES.map(([val, label, blurb]) => (
          <button
            key={val}
            type="button"
            // Only 'optional' carries a price, so any other choice clears one
            // that is sitting there. The server and the DB both refuse the
            // combination anyway; clearing it here means he never has to be told.
            onClick={() => set({ tria_status: val, ...(val === 'optional' ? {} : { tria_premium: '' }) })}
            className={cn(
              'min-w-[9rem] flex-1 rounded-card border px-3 py-2.5 text-left',
              status === val ? 'border-accent bg-accent-soft' : 'border-border bg-bg/30 hover:border-border-strong'
            )}
          >
            <span className={cn('block text-sm font-medium', status === val ? 'text-accent' : 'text-text')}>{label}</span>
            <span className="mt-0.5 block text-[11px] text-text-muted">{blurb}</span>
          </button>
        ))}
      </div>

      {/* NO autoFocus (David, 2026-08-07: "it always brings up that screen to
          the bottom rather than the top"). It was right when TRIA had its own
          PAGE and this was the only field on it. TRIA folded into the foot of
          the Coverage step on 2026-08-01 and the autoFocus came with it, so
          every open of that step scrolled the sheet past the limits to the last
          control on the page. A focus that moves the viewport belongs to the
          field the broker came for, and on this page that is the limits. */}
      {/* ONE HOME FOR THE AMOUNT. Once the liability limits branch, the TRIA
          premium rides the liability leg (Allianz priced 675 against $20M and
          840 against $25M) and each block above carries its own box. Leaving
          this one would render leg 1's amount TWICE on one page, which is how
          two copies of a fact start disagreeing. The three-state ANSWER stays
          here: that is one fact about the quote whatever the limits do. */}
      {status === 'optional' && perLeg ? (
        <p className="text-[11.5px] text-text-muted">
          Each liability limit above carries its own TRIA amount, beside the limit it prices.
        </p>
      ) : null}
      {status === 'optional' && !perLeg ? (
        <Field label="TRIA premium">
          <MoneyInput value={form.tria_premium} onChange={(v) => set({ tria_premium: v })} placeholder="136" />
          <Helper>Required, and greater than zero.</Helper>
        </Field>
      ) : null}

      {!status ? (
        <p className="rounded-card border border-border bg-bg/40 px-3 py-2 text-[11.5px] text-text-muted">
          Not answered.
        </p>
      ) : (
        <button
          type="button"
          onClick={() => set({ tria_status: null, tria_premium: '' })}
          className="text-[11px] text-text-dim underline decoration-dotted underline-offset-2 hover:text-text-muted"
        >
          Clear the answer
        </button>
      )}
    </div>
  );
}

function PilotsStep({
  form, set, pilots, rows, onEditRoster, defaulted,
  // WHO IS ON THIS OFFER is a different fact from who is on the deal, with a
  // different door. David, 2026-09-09: "there needs to be a difference between
  // taking a pilot off a roster and taking him off the quote."
  excludedPilots = [], isAlternate = false, onSetMembership, membershipBusy = null,
}) {
  const mark = (k) => (defaulted?.has(k) ? 'default' : null);
  const warn = opwWarning(form);
  const npo = form.named_pilots_only;
  // Shared requirements, each scoped to a tail or to the whole policy.
  const commonReqs = Array.isArray(form.common_requirements) ? form.common_requirements : [];
  const setCommonReq = (i, patch) => set({
    common_requirements: commonReqs.map((r, j) => (j === i ? { ...r, ...patch } : r)),
  });
  const tailOptions = (rows || []).map((r) => r.tail_display || r.tail_number).filter(Boolean);
  // The aircraft THIS offer covers, for the per-tail exclusion chips.
  const offerTails = (rows || []).filter((r) => r.on_quote !== false).map((r) => r.tail_display || r.tail_number).filter(Boolean);
  const toggleRating = (r) => set({
    opw_ratings: form.opw_ratings.includes(r)
      ? form.opw_ratings.filter((x) => x !== r)
      : [...form.opw_ratings, r],
  });
  return (
    <div className="space-y-3" data-field="pilot_clause pilot_list">
      <label className="flex items-center gap-2 rounded-card border border-border bg-bg/30 px-3 py-2">
        <input
          type="checkbox"
          checked={npo}
          onChange={(e) => set({ named_pilots_only: e.target.checked })}
          className="h-4 w-4 rounded border-border bg-bg accent-accent"
        />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-text">Named Pilots Only</div>
          <div className="text-[10px] text-text-dim">
            Only the listed pilots are covered. The open pilot fields below clear on save.
          </div>
        </div>
      </label>

      {npo ? null : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {/* DEFAULTED TO PRIVATE. 163 of the 171 quotes that record an open
                pilot certificate say Private. Never defaulted on a Named Pilots
                Only quote (coverageDefaults guards it), because those fields are
                cleared on save and hidden here. */}
            <Field label="Certificate" tag={mark('opw_certificate')}>
              <select value={form.opw_certificate} onChange={(e) => set({ opw_certificate: e.target.value })} className={inputClass}>
                <option value="">— not answered —</option>
                {OPW_CERTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Ratings">
              <div className="flex flex-wrap gap-1.5">
                {OPW_RATINGS.map((r) => {
                  const on = form.opw_ratings.includes(r);
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => toggleRating(r)}
                      className={cn(
                        'rounded-pill border px-2.5 py-1 text-[11px] font-medium',
                        on ? 'border-accent bg-accent-soft text-accent' : 'border-border bg-bg text-text-muted hover:border-border-strong'
                      )}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Total hours">
              <Input value={form.opw_total_hours} onChange={(e) => set({ opw_total_hours: e.target.value })} placeholder="500" inputMode="decimal" />
            </Field>
            <Field label="Type hours (free text)">
              <Input value={form.opw_type_hours} onChange={(e) => set({ opw_type_hours: e.target.value })} placeholder="e.g. 50 hrs retract" />
            </Field>
            <Field label="Make/model hours">
              <Input value={form.opw_make_model_hours} onChange={(e) => set({ opw_make_model_hours: e.target.value })} placeholder="25" inputMode="decimal" />
            </Field>
          </div>
          <Field label="Full clause (paste it if it will not break into the boxes above)">
            <AutoTextarea
              minRows={3}
              value={form.opw_full_text}
              onChange={(v) => set({ opw_full_text: v })}
              placeholder="The carrier's open pilot clause, as worded"
            />
          </Field>
          {warn ? (
            <div className="flex items-start gap-2 rounded-card border border-warning/40 bg-warning/10 p-2.5 text-[11px] leading-relaxed text-warning">
              <AlertTriangle size={13} strokeWidth={1.5} className="mt-0.5 shrink-0" />
              <span>{warn}</span>
            </div>
          ) : null}
        </>
      )}

      {/* One row per pilot carrying two independent facts: the carrier's
          DECISION (a submitted PHF is not an approval) and any REQUIREMENT it
          imposed. A pilot can be approved and still owe 25 hours dual. The
          per-pilot text prints only on that pilot's application, which is the
          2026-05-22 bug (one requirement applied to all four pilots) staying
          fixed. */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">On this offer</p>
        </div>
        {pilots.length === 0 ? (
          <p className="text-[11px] text-text-dim">No pilots on this deal yet.</p>
        ) : pilots.map((p) => {
          // UNOFFERABLE. A roster pilot with no CRM Contact has no record to
          // carry a carrier decision or a requirement against, so the gate
          // stays closed. What changes is that he is no longer INVISIBLE: the
          // list used to come from Approved_Pilots alone, so David Elvin was
          // absent from a quote covering him and nothing said so. A shorter
          // list than the roster, on this field, means a pilot nobody has
          // noticed is uninsured.
          if (p.offerable === false) {
            return (
              <div key={p.roster_key} className="space-y-1.5 rounded-card border border-warning/40 bg-warning-soft p-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-[120px] flex-1 text-sm font-medium text-text">{p.name || 'Unnamed pilot'}</span>
                  <span className="rounded-pill border border-warning/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning">
                    Cannot be offered
                  </span>
                </div>
                <p className="text-[11px] leading-relaxed text-text">
                  {p.blocked_reason === 'no_crm_contact'
                    ? `${p.name || 'This pilot'} is on the roster with no CRM contact, so the carrier's decision cannot be recorded and they cannot be named on the policy. Open the roster and confirm them to create the contact, then reopen this step.`
                    /* A reason this build does not recognise still gets NAMED. Rendering
                       an empty row for it would be the same silence, one layer up. */
                    : `${p.name || 'This pilot'} cannot be named on the policy (${p.blocked_reason || 'reason not stated'}). Open the roster to resolve it.`}
                </p>
                <button
                  type="button"
                  onClick={onEditRoster}
                  className="rounded-pill border border-warning/50 bg-bg px-2.5 py-0.5 text-[11px] font-medium text-warning hover:border-warning"
                >
                  Fix in the roster
                </button>
              </div>
            );
          }
          const cur = form.pilot_approval_by_contact[String(p.contact_id)] || {};
          const setApproval = (patch) => set({
            pilot_approval_by_contact: {
              ...form.pilot_approval_by_contact,
              [String(p.contact_id)]: { status: '', condition_text: '', name: p.name, ...cur, ...patch },
            },
          });
          // Conditional is not a choice any more, it is what a condition MAKES
          // an approval (item 9). A stored 'conditional' therefore shows as
          // Approved with its text underneath, which is what it always meant.
          const decision = cur.status === 'conditional' ? 'approved' : (cur.status || '');
          // The one per-pilot requirement box IS the condition. Derive the
          // status the SAME way the save does, so the card and the saved row
          // can never disagree (item 4: the helper used to compare against the
          // LOADED value and insisted "conditional with no condition stated"
          // beside a populated box and a dropdown reading Approved).
          const reqText = (form.pilot_requirements_by_name[p.name] || '').trim();
          const derivedStatus = encodeApprovalStatus({ ...cur, condition_text: reqText });
          // The one genuine carry-forward: a row recorded conditional by the
          // carrier with no condition text, untouched. It stays conditional and
          // still deserves explaining, but only while it is actually true.
          const bareConditional = derivedStatus === 'conditional' && !reqText;
          return (
            <div key={p.roster_key || p.contact_id} className="space-y-2 rounded-card border border-border bg-bg/30 p-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-[120px] flex-1 text-sm font-medium text-text">{p.name}</span>
                <select
                  value={decision}
                  onChange={(e) => setApproval({ status: e.target.value, touched: true })}
                  className={cn(inputClass, 'w-44')}
                  aria-label={`Carrier decision for ${p.name}`}
                >
                  <option value="">— no decision —</option>
                  <option value="approved">Approved</option>
                  <option value="declined">Declined</option>
                  <option value="excluded">Excluded</option>
                </select>
                {/* THE QUOTE-SCOPED CONTROL, on the pilot, where the hand
                    already is. This is the gesture whose absence caused the
                    2026-09-09 incident: with no way to say "this offer is
                    without him", the only reachable door was the deal roster.
                    On an alternate it edits this quote. On an original it FORKS,
                    because a quote keeps the risk makeup it was rated on and a
                    different makeup is a different record. */}
                <button
                  type="button"
                  disabled={membershipBusy === (p.contact_id || p.roster_key) || !p.contact_id}
                  onClick={() => onSetMembership?.(p, false)}
                  className="shrink-0 rounded-pill border border-border bg-bg px-2.5 py-0.5 text-[11px] text-text-muted hover:border-border-strong hover:text-text disabled:opacity-50"
                  title={isAlternate
                    ? `Take ${p.name} off this alternate`
                    : `Create an alternate without ${p.name}, leaving this quote as it is`}
                >
                  {isAlternate ? 'Remove from this alternate' : `Fork without ${p.name}`}
                </button>
              </div>
              {decision === 'declined' || decision === 'excluded' ? (
                /* A DECLINE IS NOT AN EXCLUSION (David, 2026-09-30). Declined: the
                   carrier will not name him, and he may still fly under the Open
                   Pilot Warranty if he meets it, so nothing prints. Excluded: he
                   may not fly at all; this offer's card lists him under Excluded
                   Pilots in the carrier's words, and binding this offer writes the
                   exclusion onto the policy. Neither touches the deal roster,
                   because another carrier may answer differently about the same
                   pilot (Epix Air / Brian Dorcey: Beacon excluded him). */
                <div className="space-y-1.5 rounded-card border border-warning/40 bg-warning-soft p-2">
                  <p className="text-[11px] leading-relaxed text-text">
                    {decision === 'excluded'
                      ? `Excluded by the carrier. ${p.name} stays on the deal roster. This offer's card lists ${p.name} under Excluded Pilots in the wording below, and binding this offer excludes ${p.name} on the policy.`
                      : `Declined by the carrier. ${p.name} is not named on this offer and does not print on its card. ${p.name} may still fly under the Open Pilot Warranty when the requirements are met. To take ${p.name} off the deal entirely, use the roster.`}
                  </p>
                  {decision === 'declined' ? (
                    <button
                      type="button"
                      onClick={onEditRoster}
                      className="rounded-pill border border-warning/50 bg-bg px-2.5 py-0.5 text-[11px] font-medium text-warning hover:border-warning"
                    >
                      Open the roster
                    </button>
                  ) : null}
                </div>
              ) : null}
              {/* EXCLUDED ON SOME AIRCRAFT, NAMED ON THE REST (Ben Rothstein,
                  2026-09-30). Only when the offer covers more than one aircraft,
                  and only under a decision that names him: a whole-policy
                  exclusion or a decline has nothing per aircraft to say. */}
              {decision === 'approved' && offerTails.length > 1 ? (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] text-text-muted">Excluded on:</span>
                  {offerTails.map((t) => {
                    const on = (cur.excluded_tails || []).some((x) => String(x).toUpperCase() === t.toUpperCase());
                    return (
                      <button
                        key={t}
                        type="button"
                        aria-pressed={on}
                        onClick={() => setApproval({
                          touched: true,
                          excluded_tails: on
                            ? (cur.excluded_tails || []).filter((x) => String(x).toUpperCase() !== t.toUpperCase())
                            : [...(cur.excluded_tails || []), t],
                        })}
                        className={cn('rounded-pill border px-2 py-0.5 text-[11px]',
                          on ? 'border-warning bg-warning-soft text-warning' : 'border-border bg-bg text-text-muted hover:border-border-strong hover:text-text')}
                      >
                        {t}
                      </button>
                    );
                  })}
                  {(cur.excluded_tails || []).length ? (
                    <span className="text-[11px] text-text-dim">Named on the rest. Prints under Excluded Pilots for these aircraft.</span>
                  ) : null}
                </div>
              ) : null}
              {bareConditional ? (
                <p className="text-[11px] text-text-muted">
                  Recorded conditional by the carrier with no condition stated. Type the requirement below, or pick a decision to change it.
                </p>
              ) : null}
              <AutoTextarea
                value={form.pilot_requirements_by_name[p.name] || ''}
                onChange={(v) => set({
                  pilot_requirements_by_name: { ...form.pilot_requirements_by_name, [p.name]: v },
                })}
                placeholder={decision === 'excluded'
                  ? `The carrier's exclusion wording for ${p.name}, e.g. may not operate the aircraft controls at any time`
                  : decision === 'declined'
                  ? `Why ${p.name} was declined, and anything the carrier still requires`
                  : `Anything the carrier requires of ${p.name}, e.g. 25 hrs dual before solo`}
              />
              {reqText ? (
                <p className="text-[11px] text-text-muted">Approved with conditions. This prints on {p.name}&rsquo;s application.</p>
              ) : null}
            </div>
          );
        })}

        {/* EXCLUDED BY THE CARRIER, NOT ON THE ROSTER (David, 2026-09-30). A
            carrier can exclude a person who is not a pilot on this deal, and
            adding him to the roster only to mark him excluded is the wrong
            round trip. These rows carry a name and no contact id, print under
            Excluded Pilots on this offer's card, and, if this offer binds, on
            the policy. They never touch the roster. */}
        <div className="space-y-2">
          <div className="text-xs font-medium text-text-muted">Excluded by the carrier, not on the roster</div>
          <p className="text-[11px] text-text-dim">
            Someone the carrier names as excluded who is not a pilot on this deal. Prints under Excluded Pilots on this offer only.
          </p>
          {(form.carrier_excluded_names || []).map((x, i) => (
            <div key={i} className="space-y-1.5 rounded-card border border-border bg-bg/30 p-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <input
                  value={x.name || ''}
                  onChange={(e) => set({ carrier_excluded_names: (form.carrier_excluded_names || []).map((y, j) => (j === i ? { ...y, name: e.target.value } : y)) })}
                  placeholder="Full name"
                  className={cn(inputClass, 'min-w-[160px] flex-1')}
                  aria-label="Excluded person's name"
                />
                <button
                  type="button"
                  onClick={() => set({ carrier_excluded_names: (form.carrier_excluded_names || []).filter((_, j) => j !== i) })}
                  className="shrink-0 rounded-pill border border-border bg-bg px-2.5 py-0.5 text-[11px] text-text-muted hover:border-border-strong hover:text-text"
                >
                  Remove
                </button>
              </div>
              <AutoTextarea
                value={x.condition_text || ''}
                onChange={(v) => set({ carrier_excluded_names: (form.carrier_excluded_names || []).map((y, j) => (j === i ? { ...y, condition_text: v } : y)) })}
                placeholder={`The carrier's exclusion wording, e.g. ${x.name || 'this person'} may not operate the aircraft controls at any time`}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={() => set({ carrier_excluded_names: [...(form.carrier_excluded_names || []), { name: '', condition_text: '' }] })}
            className="rounded-pill border border-border bg-bg px-2.5 py-0.5 text-[11px] text-text-muted hover:border-border-strong hover:text-text"
          >
            + Add excluded pilot
          </button>
        </div>

        {/* NOT ON THIS OFFER. Normally empty. When it is not, this is the ONLY
            place the exclusion is visible: a pilot taken off the deal vanishes
            from mergeRosterPilots (it filters status='removed'), so his stored
            entry used to become unreachable through every door. Connor Meng sat
            like that on two Acceleration quotes on 2026-09-10, printing his
            student-pilot training requirement on the card that excluded him. */}
        {excludedPilots.length > 0 ? (
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Not on this offer</p>
            {excludedPilots.map((p) => (
              <div key={p.roster_key || p.contact_id} className="space-y-1.5 rounded-card border border-border bg-bg/20 p-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-[120px] flex-1 text-sm font-medium text-text-muted line-through">{p.name || 'Unnamed pilot'}</span>
                  <button
                    type="button"
                    disabled={membershipBusy === (p.contact_id || p.roster_key)}
                    onClick={() => onSetMembership?.(p, true)}
                    className="rounded-pill border border-border bg-bg px-2.5 py-0.5 text-[11px] text-text-muted hover:border-border-strong hover:text-text disabled:opacity-50"
                  >
                    Put back on this quote
                  </button>
                </div>
                {/* THREE DIFFERENT STATES, WORDED DIFFERENTLY. A deliberate
                    exclusion is a decision; a quote naming someone the deal does
                    not carry is a CONFLICT, and it is the state Meng's $3,445
                    alternate was actually in. Rendering them the same sentence
                    would hide the one that needs resolving. */}
                <p className="text-[11px] leading-relaxed text-text-dim">
                  {p.blocked_reason === 'named_but_off_deal'
                    ? `${p.name || 'This pilot'} is named on this quote but is not on the deal roster, so this offer cannot be presented until that is resolved. Put him back on the deal, or take him off this quote.`
                    : p.blocked_reason === 'excluded_and_off_deal'
                      ? `${p.name || 'This pilot'} was taken off this offer and has since left the deal roster. Putting him back here covers him on this offer only; use the deal roster below if he should be back on the deal.`
                      : `${p.name || 'This pilot'} is on the deal but not on this offer.`}
                </p>
              </div>
            ))}
          </div>
        ) : null}

        {/* THE DEAL DOOR, NAMED AS ONE, AND MOVED AWAY FROM THE QUOTE CONTROLS.
            It used to read "Add or remove a pilot" and sit in the header above,
            among controls that are every one of them quote-scoped. David, on the
            removal that caused this: "it looked wrong. but i went with it because
            it was placed in a stupid spot without warning." A control's placement
            is a claim about its scope. */}
        <div className="flex items-center justify-between gap-2 border-t border-border pt-2">
          <p className="text-[11px] leading-relaxed text-text-dim">
            Changing the deal roster affects every quote on this deal, not just this one.
          </p>
          <button
            type="button"
            onClick={onEditRoster}
            className="shrink-0 rounded-pill border border-border bg-bg px-2.5 py-0.5 text-[11px] text-text-muted hover:border-border-strong hover:text-text"
          >
            Edit the deal roster
          </button>
        </div>
        {/* Requirements that apply to EVERY pilot, each carrying the aircraft
            it belongs to. David: "a Malibu on a policy with a Warrior, Malibu
            requires recurrent training, Warrior won't... why not just append it
            under the aircraft it belongs?" A tail-scoped one prints under that
            aircraft; an All-aircraft one prints once after the last. Neither
            ever repeats per pilot. */}
        <div className="space-y-2 rounded-card border border-border bg-bg/30 p-2.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Requirements for every pilot</p>
            <button
              type="button"
              onClick={() => set({ common_requirements: [...commonReqs, { tail_number: '', requirement: '' }] })}
              className="inline-flex items-center gap-1 rounded-pill border border-border bg-bg px-2.5 py-0.5 text-[11px] text-text-muted hover:border-border-strong hover:text-text"
            >
              <Plus size={12} /> Add
            </button>
          </div>
          {commonReqs.length === 0 ? (
            <p className="text-[11px] text-text-dim">Nothing that applies to every pilot on this quote.</p>
          ) : commonReqs.map((row, i) => (
            <div key={i} className="space-y-1.5">
              <div className="flex items-center gap-2">
                <select
                  value={row.tail_number || ''}
                  onChange={(e) => setCommonReq(i, { tail_number: e.target.value })}
                  className={cn(inputClass, 'w-44')}
                  aria-label="Aircraft this requirement applies to"
                >
                  <option value="">All aircraft</option>
                  {tailOptions.map((t) => <option key={t} value={t}>{t} only</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => set({ common_requirements: commonReqs.filter((_, j) => j !== i) })}
                  className="ml-auto text-text-dim hover:text-accent"
                  aria-label="Remove requirement"
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <AutoTextarea
                value={row.requirement || ''}
                onChange={(v) => setCommonReq(i, { requirement: v })}
                placeholder={row.tail_number
                  ? `What every pilot of ${row.tail_number} must do, e.g. annual recurrent training in make and model`
                  : 'What every pilot on the policy must do, whatever they fly'}
              />
              {(row.requirement || '').trim() ? (
                <p className="text-[11px] text-text-muted">
                  {row.tail_number
                    ? `Prints once under ${row.tail_number}, not on the other aircraft.`
                    : 'Prints once at the end of the pilot list, on every aircraft.'}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// THE UNDERWRITER IS A LOOKUP, NOT A TEXT BOX (David, 2026-08-01). The names
// come off `carriers.contacts[]`, which is where this org's carrier contacts
// already live: each row carries name, role, email and a crm_contact_id to the
// real CRM Contact. Zoho Contacts hold no Vendor_Name links, so that array is
// the store, not a cache of one.
//
// READ ONLY, DELIBERATELY. David's rule (CLAUDE.md, One Fact One Writer): a
// quote save may call an existing writer, but it may not become a second one.
// The only write to `carriers` in the codebase is inline in
// routes/admin-carriers.routes.js PUT /:slug, behind validateCarrierUpdate.
// There is no service to call, so typing a new name here records it on the
// QUOTE and nowhere else. Adding it to the carrier stays an admin action until
// that writer is extracted. A free-typed name is still accepted: the list is a
// shortcut, never a gate, and an underwriter nobody has met yet is normal.
function UnderwriterField({ value, onChange, contacts }) {
  const names = (Array.isArray(contacts) ? contacts : [])
    .map((c) => (c && typeof c.name === 'string' ? c.name.trim() : ''))
    .filter(Boolean);
  const listId = names.length ? 'wizard-underwriter-options' : undefined;
  return (
    <Field label="Underwriter">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="who at the carrier"
        list={listId}
      />
      {listId ? (
        <datalist id={listId}>
          {names.map((n) => <option key={n} value={n} />)}
        </datalist>
      ) : null}
      <Helper>
        {names.length
          ? 'The carrier’s contacts on file. Type any other name freely.'
          : 'No contacts on file for this carrier yet. Add them under Admin › Carriers.'}
      </Helper>
    </Field>
  );
}

function RestStep({ form, set, carrierContacts }) {
  const addPart = () => set({
    coverage_parts: [...form.coverage_parts, {
      part_type: PART_TYPE_PICKLIST[0], limit: '', sub_limit: '', broker_notes: '',
      client_blurb: '', optional: false, elected_default: false, price_mode: 'flat', premium: '', pct: '',
    }],
  });
  const setPart = (i, patch) => set({
    coverage_parts: form.coverage_parts.map((r, j) => (j === i ? { ...r, ...patch } : r)),
  });
  const removePart = (i) => set({ coverage_parts: form.coverage_parts.filter((_, j) => j !== i) });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <UnderwriterField
          value={form.underwriter}
          onChange={(v) => set({ underwriter: v })}
          contacts={carrierContacts}
        />
      </div>

      <div className="space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Subjectivities</p>
        <AutoTextarea
          minRows={3}
          value={form.subject_to}
          onChange={(v) => set({ subject_to: v })}
          placeholder="One per line. Broker-internal, never shown to the client."
        />
      </div>

      <div className="space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">Coverage parts</p>
        {form.coverage_parts.length === 0 ? (
          <p className="text-[11.5px] text-text-dim">None.</p>
        ) : form.coverage_parts.map((r, i) => {
          const isPct = r.price_mode === 'liability_premium' || r.price_mode === 'hull_premium';
          return (
            <div key={r.id || i} className="space-y-2 rounded-card border border-border bg-bg/30 p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-[2fr_1fr_1fr_auto]">
                <Field label="Part type">
                  <select value={r.part_type} onChange={(e) => setPart(i, { part_type: e.target.value })} className={inputClass}>
                    {PART_TYPE_PICKLIST.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Limit">
                  <Input value={r.limit} onChange={(e) => setPart(i, { limit: e.target.value })} inputMode="decimal" />
                </Field>
                <Field label="Sub-limit">
                  <Input value={r.sub_limit} onChange={(e) => setPart(i, { sub_limit: e.target.value })} inputMode="decimal" />
                </Field>
                <div className="flex items-end justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={() => removePart(i)} aria-label="Remove part">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex items-center gap-1.5 pb-1.5 text-[12px] text-text-muted">
                  <input type="checkbox" checked={r.optional === true} onChange={(e) => setPart(i, { optional: e.target.checked })} />
                  Optional (client can elect)
                </label>
                <Field label="Pricing">
                  <select value={r.price_mode} onChange={(e) => setPart(i, { price_mode: e.target.value })} className={inputClass}>
                    <option value="flat">Flat premium</option>
                    <option value="liability_premium">% of liability premium</option>
                    <option value="hull_premium">% of hull premium</option>
                  </select>
                </Field>
                {isPct ? (
                  <Field label="Percent">
                    <Input value={r.pct} onChange={(e) => setPart(i, { pct: e.target.value })} inputMode="decimal" placeholder="10" />
                  </Field>
                ) : (
                  <Field label="Premium">
                    <Input value={r.premium} onChange={(e) => setPart(i, { premium: e.target.value })} inputMode="decimal" />
                  </Field>
                )}
                {r.optional ? (
                  <label className="flex items-center gap-1.5 pb-1.5 text-[12px] text-text-muted">
                    <input type="checkbox" checked={r.elected_default === true} onChange={(e) => setPart(i, { elected_default: e.target.checked })} />
                    Checked by default
                  </label>
                ) : null}
              </div>
            </div>
          );
        })}
        <Button type="button" variant="outline" size="sm" onClick={addPart} className="gap-1.5">
          <Plus size={14} /> Add part
        </Button>
      </div>

      {/* Two prose fields, and which side of the wall each sits on is the whole
          point of keeping them apart. */}
      <Field label="Coverage enhancements (shown to the client)">
        <AutoTextarea
          value={form.coverage_enhancements}
          onChange={(v) => set({ coverage_enhancements: v })}
          placeholder="e.g. includes premises liability, spare parts, unearned hull premium"
          maxLength={2000}
        />
        <Helper>Shown to the client.</Helper>
      </Field>
      <Field label="Broker notes">
        <AutoTextarea
          value={form.broker_notes}
          onChange={(v) => set({ broker_notes: v })}
          placeholder="Internal. Not shown to the client."
          maxLength={2000}
        />
      </Field>
    </div>
  );
}

// QuickPicks moved to components/quote/QuoteControls.jsx (2026-11-03), shared with the coverage-term sheets.
